/*
  Deconstruct GlamAR Skin Analysis integration.

  This asset is the merchant-side orchestration layer for the Deconstruct
  Skin Analysis page. It is responsible for loading and initializing the
  GlamAR SDK, listening to SDK events, gating result access behind the
  Kwikpass login flow, recording completed analysis details in the Boltic
  proxy, rendering the recommendations and regimen screens, and keeping
  Shopify cart interactions in sync across web and Appbrew contexts.

  Important integration contract:
  - The SDK is initialized exactly once per browser page lifecycle.
  - The SDK always mounts into #cg-sdk-container.
  - Result, login, recommendation, and cart flows are event-driven from the
    GlamAR SDK callbacks and should remain independent from the home-page
    preload bridge in global.js.

  High-level customer journey:
  1. Load the GlamAR wrapper and initialize the Skin Analysis iframe.
  2. Let the customer complete analysis in the SDK-owned iframe.
  3. Normalize the result payload into skin type and top concerns.
  4. If the customer is anonymous, open Kwikpass login and pause the result
     handoff until login is confirmed.
  5. Persist the completed analysis metadata through the Boltic proxy.
  6. Render Deconstruct's recommendation journey outside the SDK iframe.
  7. Resolve recommended products to Shopify variants and add them to cart.
*/
console.log("[CG] >>> CHECKPOINT 1: outer script tag executing");
(function () {
  "use strict";
  console.log("[CG] >>> CHECKPOINT 2: IIFE entered");

  /*
    Prevent duplicate initialization when Shopify, preview tooling, or the
    home-page preload bridge causes this asset to be evaluated more than once.
  */
  if (window.__CG_GLAMAR_BOOTSTRAPPED) {
    console.log("[CG] !!! Already bootstrapped, exiting");
    return;
  }
  window.__CG_GLAMAR_BOOTSTRAPPED = true;
  console.log("[CG] >>> CHECKPOINT 3: bootstrap flag set");

  /*
    Initial loader state.

    The loader is shown as early as possible, before the SDK script is fetched,
    so direct page loads and preloaded home-page transitions both present a
    consistent loading state while the iframe and camera workflow are prepared.
  */
  if (document.body) {
    document.body.classList.add("cg-sdk-loading");
    var immediateLoader = document.getElementById("cg-loader");
    if (immediateLoader) immediateLoader.classList.remove("cg-hide");
  }

  /*
    Diagnostics and storefront compatibility.

    debugEnabled is intentionally false for production handover. The helpers
    remain in place so the client or integration team can temporarily enable
    verbose browser logging during QA without changing the event flow.
  */
  var debugEnabled = false;

  /*
    Some previous QA builds injected visual debug overlays. This cleanup keeps
    the live storefront free of temporary overlays if cached assets or preview
    tooling accidentally reintroduce them.
  */
  function removeAllDebugOverlays() {
    var ids = ["cg-debug-overlay", "debug-overlay", "cg-debug"];
    for (var i = 0; i < ids.length; i++) {
      var byId = document.getElementById(ids[i]);
      if (byId) byId.remove();
    }
  }

  /*
    Local debug logger. The color argument is kept for backward compatibility
    with older debug overlay code, but production logging is controlled only by
    debugEnabled.
  */
  function dlog(msg, color) {
    if (!debugEnabled) return;
    if (window.console && console.log) console.log("[GlamAR] " + msg);
  }

  /*
    Judge.me storefront widgets can throw a non-critical customizeBadges error
    while the SDK page is loading. Suppressing only that known widget error
    prevents unrelated theme issues from being hidden.
  */
  var previousWindowOnError = window.onerror;
  window.onerror = function (msg, url, line, col, err) {
    var message = String(msg || "");
    var errorMessage = String((err && err.message) || "");
    if (
      message.indexOf("jdgm.customizeBadges") !== -1 ||
      errorMessage.indexOf("jdgm.customizeBadges") !== -1
    ) {
      return true;
    }
    if (debugEnabled) {
      console.error(
        "[CG] window.onerror:",
        msg,
        "at",
        url + ":" + line + ":" + col,
        err
      );
    }
    if (typeof previousWindowOnError === "function") {
      return previousWindowOnError.apply(window, arguments);
    }
    return false;
  };
  window.addEventListener("unhandledrejection", function (e) {
    if (debugEnabled) console.error("[CG] unhandled rejection:", e.reason);
  });

  /*
    GlamAR and PixelBin integration constants.

    ACCESS_KEY and APP_ID identify the GlamAR skin-analysis configuration.
    Public PixelBin misc endpoints serve recommendation data and write
    Boltic login records without a storefront-side authorization header.
  */
  var ACCESS_KEY = "d8ecb30c-b9db-4d78-85c8-268d47794498";
  var APP_ID = "2502eb44-c2b5-4b00-bd91-8c01b037147c";
  var ORG_ID = "15926901";
  var ORG_ID_QUERY = "?orgId=" + encodeURIComponent(ORG_ID);
  var MISC_PUBLIC_API_BASE = "https://api.pixelbin.io/service/public/misc/v1.0";
  var RECOMMENDATION_URL =
    MISC_PUBLIC_API_BASE +
    "/sa-predictions/apps/" +
    APP_ID +
    "/recommendation" +
    ORG_ID_QUERY;
  var BOLTIC_PROXY_TABLE_ID = "ea3c612d-cf66-424e-bc6e-0bcc50c85988";
  var BOLTIC_RECORDS_PROXY_URL =
    MISC_PUBLIC_API_BASE +
    "/boltic-database/tables/" +
    BOLTIC_PROXY_TABLE_ID +
    "/records" +
    ORG_ID_QUERY;
  var SDK_SRC = "https://cdn.glamar.io/sdk/wrapper";
  var CONTAINER = "cg-sdk-container";

  /*
    Runtime state. These variables intentionally live inside the IIFE so they
    are private to this integration and cannot collide with theme scripts.
  */
  /*
    SDK lifecycle flags. These prevent duplicate init/start calls and help
    coordinate the loader with asynchronous SDK events.
  */
  var sdkInited = false;
  var sdkReady = false;
  var startRequested = false;
  var analysisStarted = false;
  var startupTimeoutId = null;

  /*
    Latest result and recommendation data. The SDK result is converted into a
    normalized skin profile, then that profile drives the recommendation API.
  */
  var latestSkinProfile = null;
  var latestRecommendationProducts = [];

  /*
    Shopify cart lookup caches. Product handle and cart data lookups are
    memoized because the same recommended products can be added from multiple
    screens.
  */
  var routineVariantIdCache = {};
  var routineVariantLookupPromises = {};
  var routineCartDataCache = null;
  var currentRoutinePeriod = "am";

  /*
    Shopify product JSON caches. The recommendation cards display compare-at
    pricing, descriptions, and ratings that the recommendation API may not
    return. These are filled from /products/{handle}.js and memoized by handle.
  */
  var shopifyProductDataCache = {};
  var shopifyProductDataPromises = {};

  /*
    Kwikpass login-gate timing. Polling is intentionally short-lived and only
    active while an anonymous customer is being asked to log in after analysis.
  */
  var SKIN_LOGIN_POPUP_POLL_INTERVAL_MS = 200;
  var SKIN_LOGIN_POPUP_MISSING_TICKS_TO_ABANDON = 2;
  var bolticLoginRecordSynced = false;
  var bolticLoginRecordSyncInFlight = false;
  var pendingSkinAnalysisPayloadForLogin = null;
  var skinAnalysisLoginGateActive = false;
  var kwikpassLoginListenerBound = false;
  var skinAnalysisLoginPollId = null;
  var skinAnalysisLoginBodyObserver = null;
  var skinAnalysisLoginPopupObserved = false;
  var skinAnalysisLoginPopupMissingTicks = 0;
  var skinAnalysisLoginOpenTimerId = null;

  /*
    Lead-form answers are kept in memory so validation re-renders do not erase
    what the customer already typed.
  */
  var regimenBuilderAnswers = {
    name: "",
    email: "",
    phone: ""
  };

  /*
    Currency symbol map used by the custom recommendation/regimen UI. Prices
    are rendered client-side because recommendation APIs may return different
    currency codes depending on catalog source.
  */
  var CURRENCY_SYMBOLS = [
    { code: "aed", symbol: "د.إ" },
    { code: "aud", symbol: "AU$" },
    { code: "brl", symbol: "R$" },
    { code: "cad", symbol: "CA$" },
    { code: "chf", symbol: "₣" },
    { code: "cny", symbol: "CN¥" },
    { code: "dkk", symbol: "kr" },
    { code: "eur", symbol: "€" },
    { code: "gbp", symbol: "£" },
    { code: "hkd", symbol: "HK$" },
    { code: "ils", symbol: "₪" },
    { code: "inr", symbol: "₹" },
    { code: "iqd", symbol: "ع.د" },
    { code: "jod", symbol: "د.أ" },
    { code: "jpy", symbol: "¥" },
    { code: "krw", symbol: "₩" },
    { code: "mnt", symbol: "₮" },
    { code: "mxn", symbol: "MX$" },
    { code: "myr", symbol: "RM" },
    { code: "ngn", symbol: "₦" },
    { code: "nok", symbol: "kr" },
    { code: "nzd", symbol: "NZ$" },
    { code: "pln", symbol: "zł" },
    { code: "rub", symbol: "₽" },
    { code: "sar", symbol: "﷼" },
    { code: "sek", symbol: "kr" },
    { code: "sgd", symbol: "S$" },
    { code: "thb", symbol: "฿" },
    { code: "try", symbol: "₺" },
    { code: "usd", symbol: "$" },
    { code: "zar", symbol: "R" },
    { code: "egp", symbol: "E£" },
    { code: "idr", symbol: "Rp" },
    { code: "le", symbol: "E£" }
  ];

  /*
    DOM and string helpers used by the generated recommendation HTML.

    All dynamic text is escaped before insertion because product names, API
    labels, and customer-entered details may come from external systems.
  */
  function $(id) {
    return document.getElementById(id);
  }

  /*
    Escapes untrusted text before it is injected into string-built HTML. This
    helper is used for visible copy such as product names, API labels, and form
    validation messages.
  */
  function escapeHTML(value) {
    var map = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    };
    return String(value == null ? "" : value).replace(
      /[&<>"']/g,
      function (ch) {
        return map[ch];
      }
    );
  }

  /*
    Escapes dynamic attribute values. It intentionally delegates to the same
    entity map as escapeHTML because these templates only inject quoted
    attribute strings.
  */
  function escapeAttr(value) {
    return escapeHTML(value);
  }

  /*
    Appbrew detection.

    The same asset can run inside Deconstruct's mobile-app webview or a normal
    browser storefront. Cart and navigation calls branch on this helper.
  */
  function isAppBrew() {
    return !!(window.ReactNativeWebView || window.appbrew);
  }

  /*
    SDK loading and readiness helpers.

    GlamAR exposes isLoaded(), loaded events, and iframe DOM mutations. The
    code below uses all three signals so the loader is dismissed reliably even
    when one browser or SDK timing path emits events earlier than another.
  */
  function clearStartupTimeout() {
    if (startupTimeoutId) {
      clearTimeout(startupTimeoutId);
      startupTimeoutId = null;
    }
  }

  /*
    Read the SDK's own readiness state defensively. Some browser paths may
    throw while the wrapper is still attaching itself to window.GlamAR.
  */
  function isGlamARLoaded() {
    try {
      return !!(
        window.GlamAR &&
        typeof window.GlamAR.isLoaded === "function" &&
        window.GlamAR.isLoaded()
      );
    } catch (error) {
      return false;
    }
  }

  /*
    Central loader toggle for the Skin Analysis experience. The class controls
    page-level CSS, while hidden/aria-hidden keep the loader out of the
    accessibility tree once the SDK is ready.
  */
  function setCgLoading(isLoading) {
    var l = $("cg-loader");
    if (l) {
      if (isLoading) {
        l.classList.remove("cg-hide");
        l.removeAttribute("hidden");
        l.setAttribute("aria-hidden", "false");
      } else {
        l.classList.add("cg-hide");
        l.setAttribute("hidden", "hidden");
        l.setAttribute("aria-hidden", "true");
      }
    }
    if (document.body) {
      if (isLoading) document.body.classList.add("cg-sdk-loading");
      else document.body.classList.remove("cg-sdk-loading");
    }
  }

  /*
    Called when any reliable SDK-ready signal is observed. If the customer has
    already requested analysis before the iframe finished loading, this resumes
    the pending start request.
  */
  function markSDKReady() {
    sdkReady = true;
    clearStartupTimeout();
    setCgLoading(false);
    if (startRequested) startSkinAnalysis();
  }

  /*
    Starts the GlamAR Skin Analysis flow exactly once. The SDK owns the camera
    and analysis screens after this command.
  */
  function startSkinAnalysis() {
    if (analysisStarted) return;
    if (!window.GlamAR || !window.GlamAR.skinAnalysis) return;
    try {
      window.GlamAR.skinAnalysis("start");
      analysisStarted = true;
    } catch (e) {
      console.error("[CG] skinAnalysis start threw:", e);
    }
  }

  /*
    Loads the GlamAR wrapper script or waits for an existing copy.

    The home preload path and the direct page path can both attempt to prepare
    the wrapper, so this function first checks for an existing script/global
    before appending another script tag.
  */
  function loadSDKScript() {
    return new Promise(function (resolve, reject) {
      console.log(
        "[CG] >>> CHECKPOINT 5: loadSDKScript | GlamAR exists:",
        !!window.GlamAR
      );
      if (window.GlamAR) {
        resolve();
        return;
      }
      var existing = document.querySelector('script[src="' + SDK_SRC + '"]');
      if (existing) {
        var t = 0;
        var iv = setInterval(function () {
          if (window.GlamAR) {
            clearInterval(iv);
            resolve();
          } else if (++t > 80) {
            clearInterval(iv);
            reject(new Error("SDK timeout from existing script"));
          }
        }, 100);
        return;
      }
      var s = document.createElement("script");
      s.src = SDK_SRC;
      s.onload = function () {
        console.log(
          "[CG] >>> CHECKPOINT 6: SDK script onload | GlamAR:",
          !!window.GlamAR
        );
        var t2 = 0;
        var iv2 = setInterval(function () {
          if (window.GlamAR) {
            clearInterval(iv2);
            resolve();
          } else if (++t2 > 80) {
            clearInterval(iv2);
            reject(new Error("SDK timeout - GlamAR never appeared"));
          }
        }, 100);
      };
      s.onerror = function () {
        console.error("[CG] !!! SDK script onerror");
        reject(new Error("SDK script failed to load"));
      };
      document.head.appendChild(s);
    });
  }

  /*
    Watches #cg-sdk-container for iframe insertion.

    This provides a fallback readiness path if the SDK's loaded event is late
    and also patches iframe camera permissions after insertion.
  */
  function monitorContainer() {
    var container = $(CONTAINER);
    if (!container) return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === "childList" && m.addedNodes.length > 0) {
          window.setTimeout(function () {
            if (isGlamARLoaded()) {
              markSDKReady();
            } else if (!sdkReady && container.children.length > 0) {
              setCgLoading(false);
            }
          }, 250);
          for (var i = 0; i < m.addedNodes.length; i++) {
            var node = m.addedNodes[i];
            if (node.tagName === "IFRAME") {
              (function (iframe) {
                setTimeout(function () {
                  var allow = iframe.getAttribute("allow") || "";
                  if (allow.indexOf("camera") === -1) {
                    iframe.setAttribute(
                      "allow",
                      (allow ? allow + "; " : "") + "camera "
                    );
                  }
                }, 2000);
              })(node);
            }
          }
        }
      });
    });
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true
    });
  }

  /*
    Main SDK initialization routine.

    It guarantees the wrapper script exists, initializes the SDK in the stable
    Deconstruct container, binds SDK listeners after init(), and starts a
    timeout that warns during QA if the SDK never emits loaded.
  */
  function initSDK() {
    console.log("[CG] >>> CHECKPOINT 4: initSDK called");
    if (sdkInited) return Promise.resolve(true);
    return loadSDKScript().then(function () {
      console.log("[CG] >>> CHECKPOINT 7: SDK loaded, calling init");
      if (!window.GlamAR) {
        throw new Error("GlamAR global missing");
      }
      if (!window.GlamAR.init) {
        throw new Error("GlamAR.init missing");
      }
      monitorContainer();
      try {
        /*
          Calling GlamAR.init after #cg-sdk-container exists is mandatory. The
          SDK creates and owns its iframe inside this container.
        */
        window.GlamAR.init(CONTAINER, ACCESS_KEY, {
          platform: "web",
          category: "skinanalysis",
          configuration: { skinAnalysis: { appId: APP_ID } },
          meta: { sdkVersion: "2.0.0" }
        });
        console.log("[CG] >>> CHECKPOINT 8: GlamAR.init() succeeded");
      } catch (e) {
        console.error("[CG] !!! GlamAR.init() threw:", e);
        throw e;
      }
      sdkInited = true;
      /*
        Bind listeners after init(). The wrapper recreates its internal event
        emitter during init, so listeners registered before init may be lost.
      */
      bindSDKEvents();
      clearStartupTimeout();
      startupTimeoutId = setTimeout(function () {
        if (!sdkReady)
          console.warn("[CG] SDK loaded event not fired after 15s");
      }, 15000);
      return true;
    });
  }
  /*
    pickOnePerType - recommendation list normalization.

    GlamAR may return multiple products for the same configured product type.
    Deconstruct's UI expects one product per type, so this keeps the first
    valid product in each known category and logs the decision trace for QA.
    Type names must match the GlamAR Console mapping after lowercase/trim.
  */
  function pickOnePerType(list) {
    if (!Array.isArray(list)) return [];
    var allowedTypes = {
      cream: true,
      serum: true,
      facewash: true,
      sunscreen: true,
      mask: true,
      toner: true,
      moisturizer: true,
      "eye-care": true,
      "lip-care": true
    };
    function normalizedTypeOf(p) {
      if (!p || !p.type) return null;
      var type = String(p.type).trim().toLowerCase();
      return allowedTypes[type] ? type : null;
    }
    var seen = Object.create(null);
    var out = [];
    var trace = [];
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      var type = normalizedTypeOf(p);
      var keep = !!type && !seen[type];
      trace.push({
        name: (p && (p.name || p.title)) || "(unnamed)",
        type: (p && p.type) || "(no type)",
        normalizedType: type,
        kept: keep
      });
      if (!keep) continue;
      seen[type] = true;
      out.push(p);
    }
    console.log(
      "[CG] pickOnePerType: " + list.length + " -> " + out.length,
      trace
    );
    return out;
  }

  /*
    SDK payload normalization.

    GlamAR event shapes can vary by SDK version and event type. These helpers
    normalize option/value/result payloads before downstream code decides
    whether the event represents a completed skin-analysis result.
  */
  /*
    Returns the most useful payload object from an SDK event. Older wrapper
    versions may place the payload directly on the event, while newer versions
    may nest it under payload, detail, data, or value.
  */
  function getEventPayload(eventData) {
    if (!eventData || typeof eventData !== "object") return {};
    if (
      Object.prototype.hasOwnProperty.call(eventData, "option") ||
      Object.prototype.hasOwnProperty.call(eventData, "options") ||
      Object.prototype.hasOwnProperty.call(eventData, "value") ||
      Object.prototype.hasOwnProperty.call(eventData, "result")
    ) {
      return eventData;
    }
    return (
      eventData.payload ||
      eventData.value ||
      eventData.detail ||
      eventData.data ||
      eventData
    );
  }

  /*
    Extracts the event option/type name and normalizes it to lowercase so later
    checks can treat "result", "Result", and equivalent SDK labels the same.
  */
  function getSkinAnalysisOption(eventData) {
    var payload = getEventPayload(eventData);
    return String(
      payload.option ||
        payload.options ||
        payload.event ||
        payload.type ||
        payload.name ||
        ""
    ).toLowerCase();
  }

  /*
    Extracts the event value/result payload. This is where the actual skin
    result, product recommendations, or nested SDK data generally lives.
  */
  function getSkinAnalysisValue(eventData) {
    var payload = getEventPayload(eventData);
    if (payload && Object.prototype.hasOwnProperty.call(payload, "value"))
      return payload.value;
    if (payload && Object.prototype.hasOwnProperty.call(payload, "result"))
      return payload.result;
    if (
      payload &&
      payload.data &&
      Object.prototype.hasOwnProperty.call(payload.data, "value")
    )
      return payload.data.value;
    return payload;
  }

  /*
    Finds the skin-analysis output object inside nested SDK payloads.
    Different wrapper versions have used outputs[0], output, skinData, data,
    and value; this recursive helper keeps the integration version-tolerant.
  */
  function getSkinOutput(value) {
    if (!value || typeof value !== "object") return null;
    if (value.outputs && value.outputs[0]) return value.outputs[0];
    if (value.output) return value.output;
    if (value.skinData) return value;
    if (value.data) return getSkinOutput(value.data);
    if (value.value) return getSkinOutput(value.value);
    return null;
  }

  /*
    Converts SDK or API labels into snake_case keys used by the recommendation
    API. Example: "Dark Circles" becomes "dark_circles".
  */
  function normalizeConcernKey(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  /*
    Maps SDK concern names and aliases to the exact concern keys accepted by
    the PixelBin recommendation API. Unknown concerns are ignored rather than
    sent upstream with unsupported names.
  */
  function mapConcernToApiKey(value) {
    var k = normalizeConcernKey(value);
    var valid = {
      acne: true,
      pores: true,
      whiteheads: true,
      eye_bags: true,
      dark_circles: true,
      post_acne_scars: true,
      wrinkles: true,
      redness: true,
      hydration: true,
      pigmentation: true
    };
    if (valid[k]) return k;
    var aliases = {
      melasma: "pigmentation",
      hyperpigmentation: "pigmentation",
      pih: "pigmentation",
      spots: "pigmentation",
      scars: "post_acne_scars",
      scar: "post_acne_scars",
      acne_scars: "post_acne_scars",
      pimples: "acne",
      breakout: "acne",
      breakouts: "acne",
      dark_circle: "dark_circles",
      bags: "eye_bags",
      fine_lines: "wrinkles",
      ageing: "wrinkles",
      aging: "wrinkles",
      texture: "pores",
      enlarged_pores: "pores",
      oiliness: "pores",
      dullness: "hydration",
      dry_skin: "hydration",
      inflammation: "redness"
    };
    return aliases[k] && valid[aliases[k]] ? aliases[k] : "";
  }

  /*
    Normalizes skin type from free-form SDK output. If the SDK returns a phrase
    such as "oily skin" or "combination type", only the supported API value is
    forwarded.
  */
  function normalizeApiSkinType(value) {
    var parts = String(value || "")
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter(Boolean);
    var valid = ["oily", "dry", "combination", "normal"];
    for (var i = 0; i < parts.length; i++) {
      if (valid.indexOf(parts[i]) !== -1) return parts[i];
    }
    return "";
  }

  /*
    Builds the recommendation request profile from the SDK result.

    The API expects one skin type and up to three concerns. Concerns are sorted
    by SDK score/value before mapping so the strongest concerns are used first.
  */
  function getSkinRecommendationProfile(value) {
    var output = getSkinOutput(value);
    var skin = output && output.skinData;
    if (!skin && output && output.total_skin_score != null) skin = output;
    if (!skin) return null;
    var skinType = normalizeApiSkinType(skin.skin_type || skin.skinType);
    var rawConcerns = Array.isArray(skin.concerns) ? skin.concerns : [];
    var concerns = [];
    var seen = {};
    rawConcerns
      .slice()
      .sort(function (a, b) {
        return (
          Number(a && (a.value != null ? a.value : a.score)) -
          Number(b && (b.value != null ? b.value : b.score))
        );
      })
      .forEach(function (concern) {
        var apiKey = mapConcernToApiKey(
          concern &&
            (concern.tech_name || concern.key || concern.name || concern.label)
        );
        if (!apiKey || seen[apiKey]) return;
        seen[apiKey] = true;
        concerns.push(apiKey);
      });
    if (!skinType) skinType = "combination";
    if (!concerns.length) concerns = ["acne", "pores", "wrinkles"];
    return {
      skinType: skinType,
      concerns: concerns.slice(0, 3)
    };
  }

  /*
    Recommendation mount and lead-capture flow.

    The SDK owns #cg-sdk-container. The custom Deconstruct result CTA and
    recommendation/regimen screens render into #cg-recommendation-mount, which
    sits immediately after the SDK container. This keeps the SDK iframe intact
    until the customer chooses to view recommendations.
  */
  /*
    Returns the recommendation mount, creating it if the Shopify template was
    customized and omitted the expected node. It is always placed after the SDK
    container so the SDK iframe remains mounted until the UI intentionally
    switches to Deconstruct's recommendation journey.
  */
  function getRecommendationMount() {
    var mount = $("cg-recommendation-mount");
    var sdk = $(CONTAINER);
    if (!mount && sdk && sdk.parentNode) {
      mount = document.createElement("div");
      mount.id = "cg-recommendation-mount";
      sdk.parentNode.insertBefore(mount, sdk.nextSibling);
    }
    return mount;
  }

  /*
    Shows the bridge CTA after analysis is complete and login requirements are
    satisfied. The SDK result remains available, but the customer explicitly
    chooses when to enter the recommendation/regimen flow.
  */
  function showRecommendationCTA() {
    var mount = getRecommendationMount();
    if (!mount) return;
    document.body.classList.remove("cg-deconstruct-reco-active");
    mount.innerHTML =
      '<div class="cg-deconstruct-result-cta-wrap">' +
      '<button class="cg-deconstruct-result-cta" id="cg-view-recommendations" type="button">' +
      '<span>View Regimen</span><span aria-hidden="true">&#8594;</span>' +
      "</button></div>";
    var button = $("cg-view-recommendations");
    if (button) {
      button.onclick = function () {
        loadRecommendationProductsAfterBuilder();
      };
    }
  }

  /*
    Regimen builder step 1 collects the customer's name. This step is kept
    lightweight so the post-analysis flow feels fast and guided.
  */
  function renderRegimenBuilderStep1(errorMessage) {
    var mount = getRecommendationMount();
    if (!mount) return;
    document.body.classList.add("cg-deconstruct-reco-active");
    mount.innerHTML =
      '<section class="cg-regimen-builder-page">' +
      '<form class="cg-regimen-builder-card" id="cg-regimen-builder-step-1" novalidate>' +
      '<div class="cg-regimen-builder-pill">Step 1 of 2</div>' +
      "<h2>YOUR SKINCARE REGIMEN BUILDER</h2>" +
      "<p>Get your personalized recommendation backed by science</p>" +
      '<label class="cg-regimen-builder-field">' +
      "<span>Your name</span>" +
      '<input id="cg-builder-name" name="name" type="text" autocomplete="name" placeholder="Your name" value="' +
      escapeAttr(regimenBuilderAnswers.name) +
      '"' +
      (errorMessage ? ' class="is-invalid"' : "") +
      ">" +
      (errorMessage
        ? '<small class="cg-regimen-builder-error">' +
          escapeHTML(errorMessage) +
          "</small>"
        : "") +
      "</label>" +
      '<button class="cg-regimen-builder-primary" type="submit">Let\'s Begin</button>' +
      "</form>" +
      "</section>";
    var form = $("cg-regimen-builder-step-1");
    var name = $("cg-builder-name");
    if (name) name.focus();
    if (form) {
      form.onsubmit = function (event) {
        event.preventDefault();
        var value = name ? String(name.value || "").trim() : "";
        regimenBuilderAnswers.name = value;
        if (value.length < 2) {
          renderRegimenBuilderStep1("Please enter your name.");
          return;
        }
        renderRegimenBuilderStep2();
      };
    }
  }

  /*
    Client-side email validation for the regimen lead form. Server-side systems
    should still validate independently if this data is later persisted.
  */
  function isValidBuilderEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());
  }

  /*
    Phone helpers store only digits for this form. This avoids formatting
    differences between desktop keyboards, mobile keyboards, and pasted values.
  */
  function getBuilderPhoneDigits(value) {
    return String(value || "").replace(/\D/g, "");
  }

  /*
    Normalizes the lead-form phone input to a compact digit-only value. The
    15-digit cap keeps pasted international numbers within a practical mobile
    number length without enforcing a single country format.
  */
  function normalizeBuilderPhoneInput(value) {
    return getBuilderPhoneDigits(value).slice(0, 15);
  }

  /*
    Wires live input and paste handling for the phone field. This keeps the
    rendered form state, the in-memory lead-form answers, and the visible input
    value synchronized across typing, mobile autofill, and pasted values.
  */
  function wireBuilderPhoneInput(input) {
    if (!input) return;
    input.oninput = function () {
      var cleanValue = normalizeBuilderPhoneInput(input.value);
      if (input.value !== cleanValue) input.value = cleanValue;
      regimenBuilderAnswers.phone = cleanValue;
    };
    input.onpaste = function (event) {
      if (!event || !event.clipboardData) return;
      event.preventDefault();
      var start =
        typeof input.selectionStart === "number"
          ? input.selectionStart
          : input.value.length;
      var end =
        typeof input.selectionEnd === "number" ? input.selectionEnd : start;
      var pastedValue = event.clipboardData.getData("text") || "";
      var nextValue =
        input.value.slice(0, start) + pastedValue + input.value.slice(end);
      input.value = normalizeBuilderPhoneInput(nextValue);
      regimenBuilderAnswers.phone = input.value;
    };
  }

  /*
    The regimen builder accepts common international mobile lengths. The form
    intentionally avoids enforcing a specific country format here because the
    store may serve app and web customers from different regions.
  */
  function isValidBuilderPhone(value) {
    var digits = getBuilderPhoneDigits(value);
    return digits.length >= 10 && digits.length <= 15;
  }

  /*
    Regimen builder step 2 collects contact details, validates them, and then
    requests product recommendations using the normalized skin profile.
  */
  function renderRegimenBuilderStep2(errors) {
    errors = errors || {};
    var mount = getRecommendationMount();
    if (!mount) return;
    document.body.classList.add("cg-deconstruct-reco-active");
    mount.innerHTML =
      '<section class="cg-regimen-builder-page">' +
      '<form class="cg-regimen-builder-card" id="cg-regimen-builder-step-2" novalidate>' +
      '<div class="cg-regimen-builder-pill">Step 2 of 2</div>' +
      "<h2>WHERE CAN WE SEND YOUR REGIMEN?</h2>" +
      "<p>Add your details to continue to skin analysis.</p>" +
      '<label class="cg-regimen-builder-field">' +
      "<span>Email address</span>" +
      '<input id="cg-builder-email" name="email" type="email" autocomplete="email" placeholder="Email address" value="' +
      escapeAttr(regimenBuilderAnswers.email) +
      '"' +
      (errors.email ? ' class="is-invalid"' : "") +
      ">" +
      (errors.email
        ? '<small class="cg-regimen-builder-error">' +
          escapeHTML(errors.email) +
          "</small>"
        : "") +
      "</label>" +
      '<label class="cg-regimen-builder-field">' +
      "<span>Mobile number</span>" +
      '<input id="cg-builder-phone" name="phone" type="tel" inputmode="tel" autocomplete="tel" enterkeyhint="done" placeholder="Mobile number" value="' +
      escapeAttr(normalizeBuilderPhoneInput(regimenBuilderAnswers.phone)) +
      '"' +
      (errors.phone ? ' class="is-invalid"' : "") +
      ">" +
      (errors.phone
        ? '<small class="cg-regimen-builder-error">' +
          escapeHTML(errors.phone) +
          "</small>"
        : "") +
      "</label>" +
      '<div class="cg-regimen-builder-actions">' +
      '<button class="cg-regimen-builder-secondary" id="cg-builder-back" type="button">Back</button>' +
      '<button class="cg-regimen-builder-primary" type="submit">Submit</button>' +
      "</div>" +
      "</form>" +
      "</section>";
    var form = $("cg-regimen-builder-step-2");
    var back = $("cg-builder-back");
    var email = $("cg-builder-email");
    var phone = $("cg-builder-phone");
    wireBuilderPhoneInput(phone);
    if (email) email.focus();
    if (back) {
      back.onclick = function () {
        regimenBuilderAnswers.email = email ? String(email.value || "") : "";
        regimenBuilderAnswers.phone = phone
          ? normalizeBuilderPhoneInput(phone.value)
          : "";
        renderRegimenBuilderStep1();
      };
    }
    if (form) {
      form.onsubmit = function (event) {
        event.preventDefault();
        var nextErrors = {};
        var emailValue = email ? String(email.value || "").trim() : "";
        var phoneValue = phone ? normalizeBuilderPhoneInput(phone.value) : "";
        if (phone) phone.value = phoneValue;
        regimenBuilderAnswers.email = emailValue;
        regimenBuilderAnswers.phone = phoneValue;
        if (!isValidBuilderEmail(emailValue)) {
          nextErrors.email = "Please enter a valid email address.";
        }
        if (!isValidBuilderPhone(phoneValue)) {
          nextErrors.phone = "Please enter a 10-15 digit mobile number.";
        }
        if (nextErrors.email || nextErrors.phone) {
          renderRegimenBuilderStep2(nextErrors);
          return;
        }
        loadRecommendationProductsAfterBuilder();
      };
    }
  }

  /*
    Starts the recommendation API call after the lead form is complete. The
    first returned product set is cached for the AM/PM recommendation tabs and
    cart variant lookup is warmed in parallel.
  */
  function loadRecommendationProductsAfterBuilder() {
    renderRecommendationLoading();
    fetchRecommendationProducts()
      .then(function (products) {
        latestRecommendationProducts = products;
        currentRoutinePeriod = "am";
        preloadProductCartContext(products);
        return enrichRecommendationProducts(products);
      })
      .then(function () {
        renderRecommendationUI();
      })
      .catch(function () {
        renderRecommendationError();
      });
  }

  /*
    Temporary loading screen shown while product recommendations are requested.
    aria-live lets assistive technology announce that the result area changed.
  */
  function renderRecommendationLoading() {
    var mount = getRecommendationMount();
    if (!mount) return;
    document.body.classList.add("cg-deconstruct-reco-active");
    mount.innerHTML =
      '<section class="cg-deconstruct-reco-page" aria-live="polite">' +
      "<h2>Your Daily Routine</h2>" +
      '<div class="cg-deconstruct-reco-card cg-deconstruct-reco-loading">Finding your routine...</div>' +
      "</section>";
  }

  /*
    Fetches top product recommendations for the current skin profile.

    If the SDK profile is unavailable for any reason, a conservative fallback
    profile is used so the UI can still recover instead of leaving the customer
    at a dead end.
  */
  function fetchRecommendationProducts() {
    var profile = latestSkinProfile || {
      skinType: "combination",
      concerns: ["acne", "pores", "wrinkles"]
    };
    return fetch(RECOMMENDATION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        skinType: profile.skinType,
        concerns: profile.concerns
      }),
      credentials: "omit"
    })
      .then(function (response) {
        return response.text().then(function (text) {
          var data = {};
          if (text) {
            try {
              data = JSON.parse(text);
            } catch (error) {
              data = {};
            }
          }
          if (!response.ok) {
            throw new Error((data && data.message) || "Recommendation failed");
          }
          return data;
        });
      })
      .then(function (data) {
        return pickFirstProductPerCategory(
          Array.isArray(data.product_list) ? data.product_list : []
        );
      });
  }

  /*
    Converts the cached recommendation API response into regimen records.

    Deconstruct wants the recommendation and regimen screens to show the same
    products. The recommendation endpoint is therefore the single product
    source; this helper only adds the step metadata needed by the existing
    regimen UI.
  */
  function mapRecommendationProductsToRoutineProducts(products) {
    var source = Array.isArray(products) ? products : [];
    var routineProducts = [];
    for (var i = 0; i < source.length; i++) {
      var product = source[i];
      if (!product || typeof product !== "object") continue;
      var routineProduct = {};
      for (var key in product) {
        if (Object.prototype.hasOwnProperty.call(product, key)) {
          routineProduct[key] = product[key];
        }
      }
      routineProduct._routineStepOrder = routineProducts.length + 1;
      routineProduct._routineStepCategory =
        getRecommendationProductCategory(product) || "Product";
      routineProduct._routinePeriods = ["routine"];
      routineProduct._routinePeriod = "routine";
      routineProduct._routineIndex = routineProducts.length;
      routineProduct._routineSelected = true;
      routineProducts.push(routineProduct);
    }
    return routineProducts;
  }

  /*
    Returns a fresh regimen-ready copy of the cached recommendation products.
    The copy step matters because the regimen UI mutates selection flags, and
    those flags should not leak back into the recommendation cards.
  */
  function getRoutineProductsFromRecommendationCache() {
    return mapRecommendationProductsToRoutineProducts(
      latestRecommendationProducts
    );
  }

  /*
    Opens the regimen screen using only the cached recommendation response.
    This preserves product parity between the recommendation and regimen
    screens and avoids the older second API call that could return a different
    product set.
  */
  function showRoutineFromRecommendationProducts() {
    /*
      Sort source products by routine step (Cleanserâ†’Serumâ†’Moisturizerâ†’Sunscreen)
      BEFORE mapping to routine products, so _routineStepOrder numbers reflect the
      intended category sequence rather than the API return order.
    */
    var sorted = sortProductsByRoutineStep(latestRecommendationProducts || []);
    var products = mapRecommendationProductsToRoutineProducts(sorted);
    if (!products.length) {
      renderRoutineError();
      return;
    }
    preloadProductCartContext(products);
    renderRoutineScreen(products);
  }

  /*
    Product normalization and display formatting for the custom UI.

    The recommendation APIs can return products from different catalog sources.
    These helpers normalize routine period, product category, pricing, and
    display labels before rendering cards or totals.
  */
  /*
    Finds the best category label for recommendation and regimen cards.
  */
  function getRecommendationProductCategory(product) {
    return String(
      (product && (product.category || product.type || product.concern)) || ""
    ).trim();
  }

  /*
    Creates a stable dedupe key for product categories. Singularizing a trailing
    "s" lets "serum" and "serums" be treated as the same category.
  */
  function normalizeCategoryKey(value) {
    var key = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    if (key.length > 3 && key.charAt(key.length - 1) === "s") {
      key = key.slice(0, -1);
    }
    return key;
  }

  /*
    Product recommendation category normalization. The APIs may use category,
    type, or concern depending on source data.
  */
  function normalizeProductCategory(product) {
    return normalizeCategoryKey(getRecommendationProductCategory(product));
  }

  /*
    Keeps the first recommendation per category. This makes the recommendation
    screen easy to scan and prevents duplicated categories from crowding the UI.
  */
  function pickFirstProductPerCategory(products) {
    if (!Array.isArray(products)) return [];
    var out = [];
    var seen = {};
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var category = normalizeProductCategory(product);
      if (!category) category = "__uncategorized_" + i;
      if (seen[category]) continue;
      seen[category] = true;
      out.push(product);
    }
    return out;
  }

  /*
    Detects serum products from their category/type label. Used by the
    "Must have" recommendation tab, which only lists serums.
  */
  function isSerumProduct(product) {
    var raw = String(
      (product && (product.category || product.type || product.concern)) || ""
    ).toLowerCase();
    return raw.indexOf("serum") !== -1;
  }

  /*
    Filters the recommendation list for the active tab:
      - "Must have" (am)  -> serums only
      - "Essentials" (pm) -> all products
  */
  function getProductsForCurrentPeriod() {
    var products = latestRecommendationProducts || [];
    if (currentRoutinePeriod === "am") {
      return products.filter(isSerumProduct);
    }
    return products.slice();
  }

  /*
    Formats a product price for display. selling_price is preferred over price
    and MRP because it represents the storefront sale value when present.
  */
  function formatPrice(product) {
    var raw =
      product && product.selling_price && Number(product.selling_price) > 0
        ? product.selling_price
        : product && product.price != null
          ? product.price
          : product && product.mrp;
    var digits = String(raw == null ? "0" : raw).replace(/[^0-9.]/g, "");
    var amount = Math.round(Number(digits) || 0);
    var currency = getCurrencySymbol((product && product.currency) || "inr");
    var prefix = currency ? currency.symbol : "";
    return (
      (prefix ||
        String((product && product.currency) || "").toUpperCase() + " ") +
      amount
    );
  }

  /*
    Returns a numeric price for totals. This mirrors formatPrice's price-source
    priority but returns a number instead of display text.
  */
  function getProductPriceValue(product) {
    var raw =
      product && product.selling_price && Number(product.selling_price) > 0
        ? product.selling_price
        : product && product.price != null
          ? product.price
          : product && product.mrp;
    var digits = String(raw == null ? "0" : raw).replace(/[^0-9.]/g, "");
    return Number(digits) || 0;
  }

  /*
    Formats totals using the known currency symbol table and thousands
    separators for readability.
  */
  function formatMoneyValue(amount, currencyCode) {
    var currency = getCurrencySymbol(currencyCode || "inr");
    var prefix = currency
      ? currency.symbol
      : String(currencyCode || "").toUpperCase() + " ";
    return prefix + formatAmount(amount);
  }

  /*
    Formats numeric totals without decimals for the compact regimen footer.
    Storefront prices are already whole-value display amounts at this stage.
  */
  function formatAmount(amount) {
    var rounded = Math.round(Number(amount) || 0);
    return String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  /*
    Looks up display symbols for currencies that can appear in recommendation
    payloads. Returning null lets the caller fall back to the raw currency code
    instead of showing a misleading symbol.
  */
  function getCurrencySymbol(code) {
    var normalized = String(code || "")
      .trim()
      .toLowerCase();
    for (var i = 0; i < CURRENCY_SYMBOLS.length; i++) {
      if (CURRENCY_SYMBOLS[i].code === normalized) return CURRENCY_SYMBOLS[i];
    }
    return null;
  }

  /*
    Shortens long Shopify product titles for compact recommendation cards.
    Merchandising separators such as "|" and " - " are removed for readability.
  */
  function shortenTitle(title) {
    var text = String(title || "Recommended product").trim();
    var pipe = text.indexOf("|");
    if (pipe > -1) text = text.slice(0, pipe).trim();
    var dash = text.indexOf(" - ");
    if (dash > 18) text = text.slice(0, dash).trim();
    return text || "Recommended product";
  }

  /*
    Converts snake_case/kebab-case API labels into "Title Case" copy used for
    concern badges and routine step labels.
  */
  function titleCase(value) {
    return String(value || "")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, function (ch) {
        return ch.toUpperCase();
      });
  }

  /*
    Removes HTML tags and collapses whitespace so Shopify body_html can be used
    as plain-text card copy.
  */
  function stripHtml(value) {
    return String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  /*
    Trims long descriptions to a card-friendly length without cutting a word in
    half. An ellipsis signals that the full copy lives on the product page.
  */
  function truncateText(value, max) {
    var text = String(value || "").trim();
    if (text.length <= max) return text;
    return text.slice(0, max).replace(/\s+\S*$/, "") + "\u2026";
  }

  /*
    Card concern badge label, e.g. concern "pigmentation" -> "For Pigmentation".
    Returns an empty string when the API did not attach a concern.
  */
  function getConcernBadgeLabel(product) {
    var raw = product && (product.concern || product.target_concern);
    var label = titleCase(raw);
    return label ? "For " + label : "";
  }

  /*
    Short, one-line product benefit. Prefers explicit API summary fields and
    returns an empty string when none are present.
  */
  function getProductShortDescription(product) {
    var raw =
      product &&
      (product.short_description ||
        product.tagline ||
        product.benefit ||
        product.subtitle ||
        product.summary);
    return raw ? stripHtml(raw) : "";
  }

  /*
    Longer descriptive paragraph. Prefers the recommendation API description,
    then falls back to the Shopify product body cached during enrichment.
  */
  function getProductLongDescription(product) {
    var raw =
      product && (product.description || product.long_description || product.details);
    if (raw && stripHtml(raw)) return truncateText(stripHtml(raw), 220);
    if (product && product._shopifyDescription) {
      return truncateText(stripHtml(product._shopifyDescription), 220);
    }
    return "";
  }

  /*
    Usage timing copy shown as "When to Use". Defaults to "Everyday" because
    the routine is positioned as a daily regimen.
  */
  function getProductUsage(product) {
    var raw = product && (product.when_to_use || product.usage || product.how_to_use);
    var text = raw ? stripHtml(raw) : "";
    return text || "Everyday";
  }

  /*
    Numeric star rating from the recommendation API. Returns 0 when no rating
    is available so the rating row can be omitted entirely.
  */
  function getProductRatingValue(product) {
    var raw =
      product &&
      (product.rating ||
        product.average_rating ||
        product.avg_rating ||
        product.ratings);
    var num = Number(raw);
    return isFinite(num) && num > 0 ? Math.min(num, 5) : 0;
  }

  /*
    Formats a rating for display: whole numbers stay integers ("4"), otherwise
    a single decimal is shown ("4.2").
  */
  function formatRating(rating) {
    return String(Math.round(Number(rating) * 10) / 10);
  }

  /*
    Renders a 5-star rating using an overlay clipped to the rating percentage,
    so half-star values display correctly. Returns "" when there is no rating.
  */
  function renderStarRating(product) {
    var rating = getProductRatingValue(product);
    if (!rating) return "";
    var star =
      '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M10 1.6l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.9l-4.94 2.6.94-5.5-4-3.9 5.53-.8z"/></svg>';
    var fiveStars = star + star + star + star + star;
    var pct = Math.max(0, Math.min(100, (rating / 5) * 100));
    return (
      '<div class="cg-deconstruct-reco-rating">' +
      '<span class="cg-stars">' +
      '<span class="cg-stars-empty">' +
      fiveStars +
      "</span>" +
      '<span class="cg-stars-full" style="width:' +
      pct +
      '%">' +
      fiveStars +
      "</span>" +
      "</span>" +
      '<span class="cg-rating-num">(' +
      escapeHTML(formatRating(rating)) +
      ")</span>" +
      "</div>"
    );
  }

  /*
    Inline sun and moon icons indicating an AM/PM friendly product. Both icons
    are shown because the recommendation API does not yet provide per-period
    metadata.
  */
  function getUsageIconsMarkup() {
    var sun =
      '<span class="cg-usage-icon cg-usage-sun" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="12" cy="12" r="4"></circle>' +
      '<path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"></path>' +
      "</svg></span>";
    var moon =
      '<span class="cg-usage-icon cg-usage-moon" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="currentColor">' +
      '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>' +
      "</svg></span>";
    return '<span class="cg-deconstruct-usage-icons">' + sun + moon + "</span>";
  }

  /*
    Concern badge markup for product cards. Empty string when no concern label.
  */
  function getConcernBadgeMarkup(product) {
    var label = getConcernBadgeLabel(product);
    return label
      ? '<span class="cg-deconstruct-concern-badge">' + escapeHTML(label) + "</span>"
      : "";
  }

  /*
    Returns the strikethrough compare-at value for a product, or 0 when there
    is no genuine discount. API mrp is preferred; Shopify compare-at (stored in
    cents during enrichment) is used as a fallback.
  */
  function getComparePriceValue(product) {
    if (!product) return 0;
    var selling = getProductPriceValue(product);
    var mrpDigits = String(product.mrp == null ? "" : product.mrp).replace(
      /[^0-9.]/g,
      ""
    );
    var mrp = Number(mrpDigits) || 0;
    if (mrp > selling) return mrp;
    if (product._shopifyCompareAt) {
      var amount = (Number(product._shopifyCompareAt) || 0) / 100;
      if (amount > selling) return amount;
    }
    return 0;
  }

  /*
    Renders the price row with an optional strikethrough compare-at price
    followed by the active selling price.
  */
  function renderPriceMarkup(product) {
    var currency = (product && product.currency) || "inr";
    var compareValue = getComparePriceValue(product);
    var compareHtml =
      compareValue > 0
        ? '<span class="cg-reco-compare">' +
          escapeHTML(formatMoneyValue(compareValue, currency)) +
          "</span>"
        : "";
    return (
      '<div class="cg-deconstruct-reco-price">' +
      compareHtml +
      '<span class="cg-reco-sale">' +
      escapeHTML(formatPrice(product)) +
      "</span>" +
      "</div>"
    );
  }

  /*
    Builds the personalized intro line for the Must Have grid, referencing the
    first one or two serums and the concerns they address.
  */
  function getRoutineIntroCopy(products) {
    var base =
      "Let's transform your skin with our AI-generated personalized routine for you.";
    var list = Array.isArray(products) ? products : [];
    var parts = [];
    for (var i = 0; i < list.length && parts.length < 2; i++) {
      var product = list[i];
      if (!product) continue;
      var name = shortenTitle(product.title || product.name);
      var concern = product.concern
        ? titleCase(product.concern).toLowerCase()
        : "";
      parts.push(
        concern ? "Use " + name + " to target " + concern : "Use " + name
      );
    }
    return parts.length ? base + " " + parts.join(" and ") + "." : base;
  }

  /*
    Routine step ordering for the Essentials timeline. Lower ranks appear first
    so the routine reads cleanser -> toner -> serum -> moisturizer -> sunscreen.
  */
  /*
    Essentials timeline order: Cleanser â†’ Serum â†’ Moisturizer â†’ Sunscreen.
    Any category not listed here (rank 99) appears after all known steps in its
    original API order.
  */
  var ROUTINE_STEP_ORDER = {
    cleanser: 1,
    facewash: 1,
    face_wash: 1,
    wash: 1,
    serum: 2,
    treatment: 2,
    toner: 2,
    essence: 2,
    moisturizer: 3,
    moisturiser: 3,
    cream: 3,
    lotion: 3,
    sunscreen: 4,
    spf: 4,
    sunblock: 4
  };

  function getRoutineStepRank(product) {
    var key = normalizeCategoryKey(getRecommendationProductCategory(product));
    return ROUTINE_STEP_ORDER[key] || 99;
  }

  /*
    Returns a copy of the product list ordered by routine step. A stable index
    is used as the tie-breaker so equal-rank products keep their API order.
  */
  function sortProductsByRoutineStep(products) {
    var list = Array.isArray(products) ? products.slice() : [];
    return list
      .map(function (product, index) {
        return { product: product, index: index };
      })
      .sort(function (a, b) {
        var diff = getRoutineStepRank(a.product) - getRoutineStepRank(b.product);
        return diff !== 0 ? diff : a.index - b.index;
      })
      .map(function (entry) {
        return entry.product;
      });
  }

  /*
    Reuses the theme's cart symbol in custom buttons by referencing the sprite
    already present in the Shopify theme.
  */
  function getCartIconMarkup() {
    return (
      '<svg class="icon icon-cart" aria-hidden="true" focusable="false">' +
      '<use href="#icon-cart"></use>' +
      "</svg>"
    );
  }

  /*
    Button content helpers keep loader/complete/pending states visually
    consistent across the regimen add-to-cart flow.
  */
  function getRegimenButtonContent(label) {
    return getCartIconMarkup() + "<span>" + escapeHTML(label) + "</span>";
  }

  /*
    Restores the regimen CTA from a transient loader/error state to normal
    icon-plus-label content while keeping the accessible label controlled by
    the visible text.
  */
  function setRegimenButtonContent(button, label) {
    if (!button) return;
    button.removeAttribute("aria-label");
    button.innerHTML = getRegimenButtonContent(label);
  }

  /*
    Shows a compact, accessible loading state while the selected regimen is
    being added to cart. The button text is replaced so repeated clicks cannot
    enqueue duplicate cart updates.
  */
  function setRegimenButtonLoading(button) {
    if (!button) return;
    button.setAttribute("aria-label", "Adding regimen");
    button.innerHTML =
      '<span class="cg-deconstruct-regimen-btn-loader" role="status" aria-label="Adding regimen"></span>';
  }

  /*
    Renders a product image with its concern badge (optional) and AM/PM icons.
  */
  function renderCardThumb(product, options) {
    options = options || {};
    var badge = options.showConcern ? getConcernBadgeMarkup(product) : "";
    var image = product.img
      ? '<img src="' +
        escapeAttr(product.img) +
        '" alt="' +
        escapeAttr(product.title || product.name) +
        '" loading="lazy" decoding="async" referrerPolicy="no-referrer">'
      : "";
    return (
      '<div class="cg-deconstruct-reco-thumb">' +
      badge +
      getUsageIconsMarkup() +
      image +
      "</div>"
    );
  }

  /*
    Renders the short benefit line and longer description paragraph. Either can
    be absent depending on the data available for the product.
  */
  function renderCardDescriptions(product) {
    var shortText = getProductShortDescription(product);
    var longText = getProductLongDescription(product);
    var html = "";
    if (shortText) {
      html += '<p class="cg-reco-short">' + escapeHTML(shortText) + "</p>";
    }
    if (longText && longText !== shortText) {
      html += '<p class="cg-reco-desc">' + escapeHTML(longText) + "</p>";
    }
    return html;
  }

  /*
    Shared product card body used by both the Must Have grid and the Essentials
    timeline. The Add to Cart button keeps the data-reco-product hook so the
    existing wiring and quiet add-to-cart flow continue to work unchanged.
  */
  function renderCardCopy(product, index) {
    return (
      '<div class="cg-deconstruct-reco-copy">' +
      "<h3>" +
      escapeHTML(shortenTitle(product.title || product.name)) +
      "</h3>" +
      renderStarRating(product) +
      renderCardDescriptions(product) +
      '<p class="cg-reco-usage"><span>When to Use:</span> ' +
      escapeHTML(getProductUsage(product)) +
      "</p>" +
      renderPriceMarkup(product) +
      '<button class="cg-deconstruct-reco-add" type="button" aria-label="Add ' +
      escapeAttr(product.title || "product") +
      ' to cart" data-reco-product="' +
      index +
      '">Add to Cart</button>' +
      "</div>"
    );
  }

  /*
    Renders the Must Have grid: serum-focused product cards with a concern
    badge, rating, description, and full-width Add to Cart button.
  */
  function renderMustHaveCards(products) {
    if (!products.length) {
      return '<div class="cg-deconstruct-reco-card cg-deconstruct-reco-empty">No products found for this routine.</div>';
    }
    return products
      .map(function (product, index) {
        return (
          '<article class="cg-deconstruct-reco-card cg-mh-card">' +
          renderCardThumb(product, { showConcern: true }) +
          renderCardCopy(product, index) +
          "</article>"
        );
      })
      .join("");
  }

  /*
    Renders the Essentials view as a vertical step timeline. Each step shows a
    "Step N: Category" label next to the product card. The products array is
    expected to already be ordered by routine step.
  */
  function renderEssentialsTimeline(products) {
    if (!products.length) {
      return '<div class="cg-deconstruct-reco-card cg-deconstruct-reco-empty">No products found for this routine.</div>';
    }
    var steps = products
      .map(function (product, index) {
        var category = getRecommendationProductCategory(product) || "Product";
        var label = "Step " + (index + 1) + ": " + titleCase(category);
        return (
          '<div class="cg-deconstruct-timeline-step">' +
          '<div class="cg-timeline-rail"><span class="cg-timeline-dot"></span></div>' +
          '<div class="cg-timeline-content">' +
          '<p class="cg-timeline-label">' +
          escapeHTML(label) +
          "</p>" +
          '<article class="cg-deconstruct-reco-card cg-timeline-card">' +
          renderCardThumb(product, { showConcern: false }) +
          renderCardCopy(product, index) +
          "</article>" +
          "</div>" +
          "</div>"
        );
      })
      .join("");
    return '<div class="cg-deconstruct-routine-timeline">' + steps + "</div>";
  }

  /*
    Renders the recommendation screen with Must Have / Essentials tabs. Must
    Have uses a product grid with a personalized intro; Essentials uses a step
    timeline. Both tabs feed the same products array into wireRecommendationUI
    so add-to-cart indices stay aligned.
  */
  function renderRecommendationUI() {
    var mount = getRecommendationMount();
    if (!mount) return;
    document.body.classList.add("cg-deconstruct-reco-active");
    var products = getProductsForCurrentPeriod();
    var allProducts = latestRecommendationProducts || [];
    var recoTotal = getRoutineSubtotal(allProducts);
    var recoCurrency = (allProducts[0] && allProducts[0].currency) || "inr";
    var listHtml;
    var productsForWiring;
    if (currentRoutinePeriod === "am") {
      productsForWiring = products;
      listHtml =
        '<p class="cg-deconstruct-routine-intro">' +
        escapeHTML(getRoutineIntroCopy(products)) +
        "</p>" +
        '<div class="cg-deconstruct-musthave-grid">' +
        renderMustHaveCards(products) +
        "</div>";
    } else {
      productsForWiring = sortProductsByRoutineStep(products);
      listHtml = renderEssentialsTimeline(productsForWiring);
    }
    mount.innerHTML =
      '<section class="cg-deconstruct-reco-page">' +
      "<h2>Your Daily Routine</h2>" +
      '<div class="cg-deconstruct-routine-tabs" role="tablist" aria-label="Product set">' +
      '<button class="' +
      (currentRoutinePeriod === "am" ? "is-active" : "") +
      '" type="button" data-routine-period="am">Must have</button>' +
      '<button class="' +
      (currentRoutinePeriod === "pm" ? "is-active" : "") +
      '" type="button" data-routine-period="pm">Full Regimen</button>' +
      "</div>" +
      listHtml +
      '<section class="cg-deconstruct-regimen-total">' +
      '<div class="cg-deconstruct-regimen-grand"><span>Total</span><strong>' +
      escapeHTML(formatMoneyValue(recoTotal, recoCurrency)) +
      "</strong></div>" +
      "</section>" +
      '<button class="cg-deconstruct-regimen-btn cg-deconstruct-regimen-add" id="cg-add-reco-regimen" type="button">' +
      getRegimenButtonContent("Add full regimen") +
      "</button>" +
      '<button class="cg-deconstruct-regimen-btn" id="cg-view-regimen" type="button" style="display:none">View Recommendation</button>' +
      "</section>";
    wireRecommendationUI(productsForWiring);
  }

  /*
    Wires interactive recommendation controls after HTML injection. Because the
    UI is rendered as strings, event handlers are attached immediately after the
    DOM is replaced.
  */
  function wireRecommendationUI(products) {
    var tabs = document.querySelectorAll("[data-routine-period]");
    for (var i = 0; i < tabs.length; i++) {
      tabs[i].onclick = function () {
        currentRoutinePeriod = this.getAttribute("data-routine-period") || "am";
        renderRecommendationUI();
      };
    }
    var buttons = document.querySelectorAll("[data-reco-product]");
    for (var b = 0; b < buttons.length; b++) {
      buttons[b].onclick = function () {
        var button = this;
        var idx = Number(this.getAttribute("data-reco-product"));
        var product = products[idx];
        if (!product) return;
        button.classList.add("is-adding");
        button.disabled = true;
        addProductQuietly(product).then(function (success) {
          button.textContent = success ? "Added" : "Try";
          if (!success) {
            button.disabled = false;
            button.classList.remove("is-adding");
          }
        });
      };
    }
    var regimen = $("cg-view-regimen");
    if (regimen) {
      regimen.onclick = function () {
        showRoutineFromRecommendationProducts();
      };
    }
    var addRecoRegimen = $("cg-add-reco-regimen");
    if (addRecoRegimen) {
      addRecoRegimen.onclick = function () {
        addFullRegimenToCart(latestRecommendationProducts, addRecoRegimen);
      };
    }
    refreshReviewWidgets();
  }

  /*
    Refreshes Judge.me review badges if the storefront review widget is present.
    The cards render their own API-based rating; this only enhances them when
    Judge.me is available on the page.
  */
  function refreshReviewWidgets() {
    try {
      if (window.jdgm && typeof window.jdgm.refreshWidgets === "function") {
        window.jdgm.refreshWidgets();
      }
    } catch (error) {}
  }

  /*
    Error state for the regimen screen. The retry button reuses the already
    fetched recommendation products.
  */
  function renderRoutineError() {
    var mount = getRecommendationMount();
    if (!mount) return;
    document.body.classList.add("cg-deconstruct-reco-active");
    mount.innerHTML =
      '<section class="cg-deconstruct-regimen-page">' +
      "<h2>Your regimen</h2>" +
      '<div class="cg-deconstruct-reco-card cg-deconstruct-reco-empty">Unable to load your regimen. Please try again.</div>' +
      '<button class="cg-deconstruct-regimen-btn" id="cg-view-regimen" type="button">Try again</button>' +
      "</section>";
    var retry = $("cg-view-regimen");
    if (retry) {
      retry.onclick = function () {
        showRoutineFromRecommendationProducts();
      };
    }
  }

  /*
    Renders the full regimen screen, including selectable products, subtotal,
    and the "Add full regimen" action.
  */
  function renderRoutineScreen(products) {
    var mount = getRecommendationMount();
    if (!mount) return;
    var list = Array.isArray(products) ? products : [];
    var selectedList = getSelectedRoutineProducts(list);
    var total = getRoutineSubtotal(selectedList);
    var currency =
      (list[0] && list[0].currency) ||
      (latestRecommendationProducts[0] &&
        latestRecommendationProducts[0].currency) ||
      "inr";
    mount.innerHTML =
      '<section class="cg-deconstruct-regimen-page">' +
      '<div class="cg-deconstruct-regimen-head">' +
      "<h2>Your regimen</h2>" +
      "<p>" +
      selectedList.length +
      " products selected</p>" +
      "</div>" +
      renderRoutinePeriod("musthave", list.filter(isSerumProduct)) +
      renderRoutinePeriod("essentials", list) +
      '<section class="cg-deconstruct-regimen-total">' +
      '<div class="cg-deconstruct-regimen-grand"><span>Total</span><strong>' +
      escapeHTML(formatMoneyValue(total, currency)) +
      "</strong></div>" +
      "</section>" +
      '<button class="cg-deconstruct-regimen-btn cg-deconstruct-regimen-add" id="cg-add-full-regimen" type="button">' +
      getRegimenButtonContent("Add full regimen") +
      "</button>" +
      "</section>";
    var addAll = $("cg-add-full-regimen");
    if (addAll) {
      addAll.onclick = function () {
        addFullRegimenToCart(getSelectedRoutineProducts(list), addAll);
      };
    }
    wireRoutineSelectionControls(list);
  }

  /*
    The regimen screen lets customers opt out of individual products before
    adding the full routine. Unselected products are excluded from totals/cart.
  */
  function getSelectedRoutineProducts(products) {
    return (products || []).filter(function (product) {
      return !product || product._routineSelected !== false;
    });
  }

  /*
    Toggles product selection and re-renders the regimen so totals and button
    state always reflect the current selection.
  */
  function wireRoutineSelectionControls(products) {
    var controls = document.querySelectorAll("[data-routine-toggle]");
    for (var i = 0; i < controls.length; i++) {
      controls[i].onclick = function () {
        var idx = Number(this.getAttribute("data-routine-toggle"));
        var product = products[idx];
        if (!product) return;
        product._routineSelected = product._routineSelected === false;
        renderRoutineScreen(products);
      };
    }
  }

  /*
    Calculates the selected routine subtotal from normalized numeric prices.
  */
  function getRoutineSubtotal(products) {
    var total = 0;
    for (var i = 0; i < products.length; i++) {
      total += getProductPriceValue(products[i]);
    }
    return total;
  }

  /*
    Renders one regimen section. Each item includes a checkbox-style toggle,
    product image, step/category label, title, and price. Sections mirror the
    recommendation tabs: "Must have" (serums) and "Essentials" (all products).
  */
  function renderRoutinePeriod(label, products) {
    if (!products.length) return "";
    return (
      '<section class="cg-deconstruct-regimen-section">' +
      "<h3>" +
      (label === "musthave" ? "MUST HAVE" : "FULL REGIMEN") +
      "</h3>" +
      '<div class="cg-deconstruct-regimen-box">' +
      products
        .map(function (product) {
          return (
            '<article class="cg-deconstruct-regimen-item' +
            (product._routineSelected === false ? " is-unselected" : "") +
            '">' +
            '<button class="cg-deconstruct-regimen-check" type="button" aria-pressed="' +
            (product._routineSelected === false ? "false" : "true") +
            '" data-routine-toggle="' +
            escapeAttr(product._routineIndex) +
            '">' +
            (product._routineSelected === false ? "" : "âœ“") +
            "</button>" +
            '<div class="cg-deconstruct-regimen-thumb">' +
            (product.img
              ? '<img src="' +
                escapeAttr(product.img) +
                '" alt="' +
                escapeAttr(product.title || product.name) +
                '" loading="lazy" decoding="async" referrerPolicy="no-referrer">'
              : "") +
            "</div>" +
            '<div class="cg-deconstruct-regimen-copy">' +
            "<p>Step " +
            escapeHTML(product._routineStepOrder || "") +
            " Â· " +
            escapeHTML(product._routineStepCategory || product.category || "") +
            "</p>" +
            "<h4>" +
            escapeHTML(shortenTitle(product.title || product.name)) +
            "</h4>" +
            "</div>" +
            '<strong class="cg-deconstruct-regimen-price">' +
            escapeHTML(formatPrice(product)) +
            "</strong>" +
            "</article>"
          );
        })
        .join("") +
      "</div>" +
      "</section>"
    );
  }

  /*
    Adds all selected regimen products to the cart as one customer action.

    The implementation first resolves all variants, updates the cart, refreshes
    the storefront cart UI, and then opens the cart drawer so the customer sees
    confirmation in the native theme experience.
  */
  function addFullRegimenToCart(products, button) {
    var list = Array.isArray(products) ? products : [];
    if (!list.length) return;
    button.disabled = true;
    button.classList.remove("is-complete");
    button.classList.add("is-pending");
    setRegimenButtonLoading(button);
    addRoutineProductsQuietly(list).then(function (payload) {
      var results = (payload && payload.results) || [];
      var allAdded =
        results.length === list.length &&
        results.every(function (success) {
          return !!success;
        });
      if (!allAdded) {
        button.disabled = false;
        button.classList.remove("is-pending");
        button.classList.remove("is-complete");
        setRegimenButtonContent(button, "Try again");
        return;
      }
      refreshCartDrawer()
        .catch(function () {
          return refreshCartBadgeOnly();
        })
        .then(function () {
          button.classList.remove("is-pending");
          button.classList.add("is-complete");
          setRegimenButtonContent(button, "Added");
          openCart();
        });
    });
  }

  /*
    Error state for the base recommendation API. Retry returns to the same
    post-builder loading flow.
  */
  function renderRecommendationError() {
    var mount = getRecommendationMount();
    if (!mount) return;
    document.body.classList.add("cg-deconstruct-reco-active");
    mount.innerHTML =
      '<section class="cg-deconstruct-reco-page">' +
      "<h2>Your Daily Routine</h2>" +
      '<div class="cg-deconstruct-reco-card cg-deconstruct-reco-empty">Unable to load recommendations. Please try again.</div>' +
      '<button class="cg-deconstruct-regimen-btn" id="cg-view-recommendations" type="button">Try again</button>' +
      "</section>";
    var retry = $("cg-view-recommendations");
    if (retry) {
      retry.onclick = function () {
        loadRecommendationProductsAfterBuilder();
      };
    }
  }

  /*
    Shopify product and variant resolution.

    Recommendation payloads do not always contain a direct Shopify variant ID.
    These helpers try explicit fields first, then product URLs/handles, then
    Shopify product JSON endpoints. Resolved IDs are cached on the product
    object and in handle-level maps to keep cart operations fast.
  */
  /*
    Extracts a Shopify product handle from an absolute or relative product URL.
    Handles are required for /products/{handle}.js lookups.
  */
  function getProductHandle(product) {
    var raw = (product && (product.product_url || product.url)) || "";
    if (!raw) return "";
    try {
      var url = new URL(raw, window.location.origin);
      var match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : "";
    } catch (error) {
      var fallback = String(raw).match(/\/products\/([^/?#]+)/);
      return fallback ? fallback[1] : "";
    }
  }

  /*
    Best-effort variant extraction from recommendation records. This supports
    direct variant fields, Shopify variant query params, and SKU patterns that
    embed a numeric variant ID.
  */
  function extractShopifyVariantIdFromProduct(product) {
    if (!product) return "";
    if (product.shopifyVariantId) return String(product.shopifyVariantId);
    var raw =
      product.variant_id ||
      product.variantId ||
      product.product_variant_id ||
      product.shopify_variant_id;
    if (raw != null && String(raw).trim()) return String(raw).trim();
    var url = product.url || product.product_url || "";
    var match = String(url).match(/[?&]variant=(\d+)/i);
    if (match) return match[1];
    var sku = String(product.sku || "").trim();
    if (sku) {
      var parts = sku.split("_").filter(Boolean);
      for (var i = 0; i < parts.length; i++) {
        if (/^\d{6,20}$/.test(parts[i])) return parts[i];
      }
    }
    return "";
  }

  /*
    Reads only explicit variant references. This is used before fallback logic
    so SKU-derived guesses do not override a true variant ID from the API.
  */
  function getExplicitProductVariantId(product) {
    if (!product) return "";
    if (product.shopifyVariantId) return String(product.shopifyVariantId);
    var raw =
      product.variant_id ||
      product.variantId ||
      product.product_variant_id ||
      product.shopify_variant_id;
    if (raw != null && String(raw).trim()) return String(raw).trim();
    var url = product.url || product.product_url || "";
    var match = String(url).match(/[?&]variant=(\d+)/i);
    return match ? match[1] : "";
  }

  /*
    Final fallback when no product handle lookup is possible. SKU is returned
    only as a last resort because some SDK cart events use SKU-like IDs.
  */
  function getProductFallbackVariantId(product) {
    return (
      extractShopifyVariantIdFromProduct(product) ||
      (product && product.sku) ||
      ""
    );
  }

  /*
    Stores the resolved variant ID on the product object so repeated UI actions
    do not refetch the same Shopify product JSON.
  */
  function setProductCartVariantId(product, variantId) {
    var id = String(variantId || "").trim();
    if (product && id) product._cartVariantId = id;
    return id;
  }

  /*
    Resolves the actual Shopify variant ID used for Ajax Cart calls.

    Resolution priority:
    1. Cached variant on the product object.
    2. Explicit variant fields from the recommendation API.
    3. Cached handle-level lookup.
    4. /products/{handle}.js lookup, preferring the first available variant.
    5. SKU/fallback value if no Shopify product lookup succeeds.
  */
  function resolveProductCartVariantId(product) {
    if (product && product._cartVariantId) {
      return Promise.resolve(String(product._cartVariantId));
    }
    var directVariantId = getExplicitProductVariantId(product);
    if (directVariantId) {
      return Promise.resolve(setProductCartVariantId(product, directVariantId));
    }
    var handle = getProductHandle(product);
    if (!handle) {
      return Promise.resolve(
        setProductCartVariantId(product, getProductFallbackVariantId(product))
      );
    }
    if (routineVariantIdCache[handle]) {
      return Promise.resolve(
        setProductCartVariantId(product, routineVariantIdCache[handle])
      );
    }
    if (!routineVariantLookupPromises[handle]) {
      routineVariantLookupPromises[handle] = fetch(
        "/products/" + encodeURIComponent(handle) + ".js",
        { credentials: "same-origin" }
      )
        .then(function (response) {
          if (!response.ok) throw new Error("product-lookup-failed");
          return response.json();
        })
        .then(function (productData) {
          var variants = Array.isArray(productData.variants)
            ? productData.variants
            : [];
          var chosen = null;
          for (var i = 0; i < variants.length; i++) {
            if (variants[i] && variants[i].available) {
              chosen = variants[i];
              break;
            }
          }
          if (!chosen && variants.length) chosen = variants[0];
          var variantId = chosen && chosen.id ? String(chosen.id) : "";
          if (variantId) routineVariantIdCache[handle] = variantId;
          return variantId || getProductFallbackVariantId(product);
        })
        .catch(function () {
          return getProductFallbackVariantId(product);
        })
        .then(function (variantId) {
          delete routineVariantLookupPromises[handle];
          return variantId;
        });
    }
    return routineVariantLookupPromises[handle].then(function (variantId) {
      if (routineVariantIdCache[handle]) {
        return setProductCartVariantId(product, routineVariantIdCache[handle]);
      }
      return setProductCartVariantId(product, variantId);
    });
  }

  /*
    Warms product variant and cart data while the customer is viewing
    recommendations. This makes later add-to-cart actions feel instant.
  */
  function preloadProductCartContext(products) {
    var list = Array.isArray(products) ? products : [];
    for (var i = 0; i < list.length; i++) {
      resolveProductCartVariantId(list[i]).catch(function () {});
    }
    if (!routineCartDataCache) {
      fetchCartData().catch(function () {});
    }
  }

  /*
    Fetches and memoizes the Shopify product JSON for a handle. The browser
    caches the GET request, so this stays cheap even though variant resolution
    hits the same endpoint.
  */
  function fetchShopifyProductData(handle) {
    if (!handle) return Promise.resolve(null);
    if (shopifyProductDataCache[handle]) {
      return Promise.resolve(shopifyProductDataCache[handle]);
    }
    if (!shopifyProductDataPromises[handle]) {
      shopifyProductDataPromises[handle] = fetch(
        "/products/" + encodeURIComponent(handle) + ".js",
        { credentials: "same-origin" }
      )
        .then(function (response) {
          if (!response.ok) throw new Error("product-data-failed");
          return response.json();
        })
        .then(function (data) {
          shopifyProductDataCache[handle] = data;
          delete shopifyProductDataPromises[handle];
          return data;
        })
        .catch(function () {
          delete shopifyProductDataPromises[handle];
          return null;
        });
    }
    return shopifyProductDataPromises[handle];
  }

  /*
    Enriches a recommendation product with Shopify catalog data used by the
    redesigned cards: product id (for review widgets), compare-at price, and a
    description fallback. The product is returned unchanged when no handle or
    Shopify record is available.
  */
  function enrichProductFromShopify(product) {
    if (!product || product._shopifyEnriched) return Promise.resolve(product);
    var handle = getProductHandle(product);
    if (!handle) return Promise.resolve(product);
    return fetchShopifyProductData(handle).then(function (data) {
      if (data) {
        if (data.id != null) product._shopifyProductId = String(data.id);
        var firstVariant =
          Array.isArray(data.variants) && data.variants.length
            ? data.variants[0]
            : null;
        if (firstVariant && firstVariant.compare_at_price) {
          product._shopifyCompareAt = firstVariant.compare_at_price;
        }
        var description = data.description || data.body_html;
        if (description) product._shopifyDescription = description;
        product._shopifyEnriched = true;
      }
      return product;
    });
  }

  /*
    Enriches all recommendation products in parallel but never blocks the UI
    for longer than a short timeout, so a slow or failed catalog lookup still
    lets the cards render with the API data already on hand.
  */
  function enrichRecommendationProducts(products) {
    var list = Array.isArray(products) ? products : [];
    var work = Promise.all(
      list.map(function (product) {
        return enrichProductFromShopify(product).catch(function () {
          return product;
        });
      })
    );
    var timeout = new Promise(function (resolve) {
      setTimeout(resolve, 2500);
    });
    return Promise.race([work, timeout]);
  }

  /*
    Boltic login record sync.

    After a skin-analysis result is available and the customer is logged in,
    this integration stores the SDK payload and normalized skin profile in the
    PixelBin Boltic proxy table. Payloads are cloned defensively so unexpected
    SDK object references do not break JSON serialization.
  */
  /*
    Emits integration events for observability. These custom events let QA or
    support tooling listen for Boltic sync success/failure without coupling to
    internal function names.
  */
  function dispatchCustomEvent(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (error) {}
  }

  /*
    Creates a JSON-safe clone of SDK data before sending it to the proxy.
    Browser objects, cyclic references, or SDK class instances are replaced by
    a small note rather than causing the entire sync to fail.
  */
  function jsonSafeCloneForBoltic(value) {
    if (value == null) return value;
    if (typeof value !== "object") return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return { _note: "non-serializable-sdk-payload", message: String(error) };
    }
  }

  /*
    Builds the metadata stored against the login record. It includes the raw
    SDK result, normalized recommendation profile, sync timestamp, and GlamAR
    app ID for traceability.
  */
  function buildBolticSkinAnalysisMeta(rawSdkPayload) {
    return {
      skinAnalysisResult: jsonSafeCloneForBoltic(rawSdkPayload),
      skinProfile: jsonSafeCloneForBoltic(latestSkinProfile),
      syncedAt: new Date().toISOString(),
      glamAppId: APP_ID
    };
  }

  /*
    Normalizes country/dial codes from cookies, local storage, JWT payloads, or
    login widgets. The returned value contains only digits and is capped to a
    realistic dial-code length.
  */
  function normalizeCountryCode(value) {
    var code = String(value || "").trim();
    if (!code) return "";
    code = code.replace(/[^\d+]/g, "");
    if (!code) return "";
    code = code.replace(/\+/g, "");
    if (code.indexOf("00") === 0) code = code.slice(2);
    if (!code || code.length > 4) return "";
    return code;
  }

  /*
    Reads a value by key across helper APIs, localStorage, sessionStorage, and
    cookies. Kwikpass/Gokwik stores login details differently across versions,
    so all storefront storage locations are checked.
  */
  function getStorageValueByKey(key) {
    try {
      if (typeof getValueFromCookiesOrLocalStorage === "function") {
        var helperValue = getValueFromCookiesOrLocalStorage(key);
        if (helperValue != null && String(helperValue).trim()) {
          return helperValue;
        }
      }
    } catch (e1) {}
    try {
      if (window.localStorage) {
        var localValue = window.localStorage.getItem(key);
        if (localValue != null && String(localValue).trim()) return localValue;
      }
    } catch (e2) {}
    try {
      if (window.sessionStorage) {
        var sessionValue = window.sessionStorage.getItem(key);
        if (sessionValue != null && String(sessionValue).trim())
          return sessionValue;
      }
    } catch (e3) {}
    try {
      var cookies = String(document.cookie || "").split(";");
      for (var i = 0; i < cookies.length; i++) {
        var part = cookies[i].split("=");
        var cookieKey = decodeURIComponent(String(part.shift() || "").trim());
        if (cookieKey === key) {
          return decodeURIComponent(part.join("=").trim());
        }
      }
    } catch (e4) {}
    return "";
  }

  /*
    Iterates likely login-related storage keys. The callback receives both the
    key and raw value so downstream extractors can infer whether the value is a
    country code, phone number, token, or nested JSON object.
  */
  function readStorageCandidateValues(callback) {
    function shouldInspectKey(key) {
      return /(kwik|gokwik|kp|notify|phone|mobile|country|dial|customer|login|token)/i.test(
        String(key || "")
      );
    }
    function readWebStorage(storage) {
      if (!storage) return;
      try {
        for (var i = 0; i < storage.length; i++) {
          var key = storage.key(i);
          if (!shouldInspectKey(key)) continue;
          callback(key, storage.getItem(key));
        }
      } catch (error) {}
    }
    readWebStorage(window.localStorage);
    readWebStorage(window.sessionStorage);
    try {
      var cookies = String(document.cookie || "").split(";");
      for (var c = 0; c < cookies.length; c++) {
        var part = cookies[c].split("=");
        var cookieKey = decodeURIComponent(String(part.shift() || "").trim());
        if (!shouldInspectKey(cookieKey)) continue;
        callback(cookieKey, decodeURIComponent(part.join("=").trim()));
      }
    } catch (error2) {}
  }

  /*
    Safely parses JSON-looking strings. Non-JSON strings are ignored so phone
    numbers and JWT tokens can continue through their own extraction paths.
  */
  function parseJsonString(value) {
    var raw = String(value || "").trim();
    if (!raw) return null;
    if (
      !(
        (raw.charAt(0) === "{" && raw.charAt(raw.length - 1) === "}") ||
        (raw.charAt(0) === "[" && raw.charAt(raw.length - 1) === "]")
      )
    ) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  /*
    Decodes the payload section of a JWT-like token. This is used only to read
    login metadata that Kwikpass may already have stored in the browser.
  */
  function decodeJwtPayload(token) {
    var parts = String(token || "").split(".");
    if (parts.length < 2 || typeof atob !== "function") return null;
    try {
      var base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      while (base64.length % 4) base64 += "=";
      var decoded = atob(base64);
      try {
        decoded = decodeURIComponent(
          decoded.replace(/(.)/g, function (match, ch) {
            return "%" + ("00" + ch.charCodeAt(0).toString(16)).slice(-2);
          })
        );
      } catch (unicodeError) {}
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  }

  /*
    Identifies storage keys likely to represent a country/dial code.
  */
  function isCountryCodeStorageKey(key) {
    return /(country|dial|phone[_-]?code|calling[_-]?code)/i.test(
      String(key || "")
    );
  }

  /*
    Extracts a country code from either a direct storage value, nested JSON, or
    a JWT payload.
  */
  function extractCountryCodeFromStorageCandidate(key, rawValue) {
    if (isCountryCodeStorageKey(key)) {
      return extractCountryCodeValue(rawValue, 0);
    }
    var parsed = parseJsonString(rawValue);
    if (parsed) {
      var parsedCountry = extractCountryCodeValue(parsed, 0);
      if (parsedCountry) return parsedCountry;
    }
    var tokenPayload = decodeJwtPayload(rawValue);
    return extractCountryCodeValue(tokenPayload, 0);
  }

  /*
    Recursively searches common country-code field names inside nested objects.
    A depth limit protects against malformed or unexpectedly deep structures.
  */
  function extractCountryCodeValue(value, depth) {
    if (value == null || depth > 5) return "";
    if (typeof value === "number") return normalizeCountryCode(value);
    if (typeof value === "string") {
      var raw = value.trim();
      var parsed = parseJsonString(raw);
      if (parsed) {
        var parsedCountry = extractCountryCodeValue(parsed, depth + 1);
        if (parsedCountry) return parsedCountry;
      }
      return normalizeCountryCode(raw);
    }
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) {
        var itemCountry = extractCountryCodeValue(value[i], depth + 1);
        if (itemCountry) return itemCountry;
      }
      return "";
    }
    if (typeof value === "object") {
      var keys = [
        "country_code",
        "countryCode",
        "dial_code",
        "dialCode",
        "phone_code",
        "phoneCode",
        "calling_code",
        "callingCode",
        "notify_country_code",
        "notifyCountryCode"
      ];
      for (var k = 0; k < keys.length; k++) {
        if (Object.prototype.hasOwnProperty.call(value, keys[k])) {
          var keyCountry = extractCountryCodeValue(value[keys[k]], depth + 1);
          if (keyCountry) return keyCountry;
        }
      }
      var nestedKeys = ["value", "data", "user", "customer", "profile"];
      for (var n = 0; n < nestedKeys.length; n++) {
        if (Object.prototype.hasOwnProperty.call(value, nestedKeys[n])) {
          var nestedCountry = extractCountryCodeValue(
            value[nestedKeys[n]],
            depth + 1
          );
          if (nestedCountry) return nestedCountry;
        }
      }
    }
    return "";
  }

  /*
    Recursively extracts a phone number from strings, arrays, objects, or nested
    customer/login payloads. If a country code and local number are present in
    the same object, they are combined for a complete phone value.
  */
  function extractPhoneValue(value, depth) {
    if (value == null || depth > 5) return "";
    if (typeof value === "number") return String(value);
    if (typeof value === "string") {
      var raw = value.trim();
      if (!raw) return "";
      if (
        (raw.charAt(0) === '"' && raw.charAt(raw.length - 1) === '"') ||
        (raw.charAt(0) === "'" && raw.charAt(raw.length - 1) === "'")
      ) {
        raw = raw.slice(1, -1).trim();
      }
      var parsed = parseJsonString(raw);
      if (parsed) {
        var parsedPhone = extractPhoneValue(parsed, depth + 1);
        if (parsedPhone) return parsedPhone;
      }
      return raw;
    }
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) {
        var itemPhone = extractPhoneValue(value[i], depth + 1);
        if (itemPhone) return itemPhone;
      }
      return "";
    }
    if (typeof value === "object") {
      var country = normalizeCountryCode(
        value.country_code ||
          value.countryCode ||
          value.dial_code ||
          value.dialCode ||
          value.phone_code ||
          value.phoneCode ||
          value.calling_code ||
          value.callingCode ||
          value.notify_country_code ||
          value.notifyCountryCode ||
          ""
      );
      var localValue =
        value.national_number ||
        value.nationalNumber ||
        value.local_number ||
        value.localNumber ||
        value.phone_number ||
        value.phoneNumber ||
        value.phone ||
        value.mobile_number ||
        value.mobileNumber ||
        value.mobile ||
        value.number ||
        "";
      var localPhone = extractPhoneValue(localValue, depth + 1);
      if (country && localPhone) {
        if (String(localPhone).trim().charAt(0) === "+") return localPhone;
        return country + " " + localPhone;
      }
      var keys = [
        "phone_number",
        "phoneNumber",
        "phone",
        "mobile_number",
        "mobileNumber",
        "mobile",
        "number",
        "value",
        "data",
        "user",
        "customer",
        "profile",
        "notify_phone_number"
      ];
      for (var k = 0; k < keys.length; k++) {
        if (Object.prototype.hasOwnProperty.call(value, keys[k])) {
          var keyPhone = extractPhoneValue(value[keys[k]], depth + 1);
          if (keyPhone) return keyPhone;
        }
      }
    }
    return "";
  }

  /*
    Converts any extracted phone representation to digits only, which is the
    format expected by the Boltic login-record proxy.
  */
  function normalizeKwikpassPhoneNumber(value) {
    var phone = extractPhoneValue(value, 0);
    if (!phone) return "";
    var digits = String(phone).trim().replace(/\D/g, "");
    return digits || "";
  }

  /*
    Combines a country code with a local phone number without duplicating the
    code if the phone already starts with it.
  */
  function combineCountryCodeWithPhone(countryCode, phoneValue) {
    var phone = normalizeKwikpassPhoneNumber(phoneValue);
    var country = normalizeCountryCode(countryCode);
    if (!phone) return "";
    if (!country) return phone;
    if (phone.indexOf(country) === 0) return phone;
    return country + phone.replace(/^0+/, "");
  }

  /*
    Reads the Kwikpass core token using the merchant environment helper when it
    is available on the storefront.
  */
  function getKwikpassTokenValue() {
    try {
      var merchantInfo = window.merchantInfo;
      if (
        !merchantInfo ||
        merchantInfo.environment == null ||
        typeof XGokwikCoreToken !== "function" ||
        typeof getValueFromCookiesOrLocalStorage !== "function"
      ) {
        return "";
      }
      var token = getValueFromCookiesOrLocalStorage(
        XGokwikCoreToken(merchantInfo.environment)
      );
      return token ? String(token).trim() : "";
    } catch (error) {
      return "";
    }
  }

  /*
    Finds the best country code signal from exact storage keys, broader storage
    scans, or token payloads.
  */
  function getKwikpassCountryCodeFromStorage() {
    var exactKeys = [
      "notify_country_code",
      "notifyCountryCode",
      "notify_dial_code",
      "notifyDialCode",
      "country_code",
      "countryCode",
      "dial_code",
      "dialCode",
      "phone_code",
      "phoneCode",
      "calling_code",
      "callingCode",
      "kp_country_code",
      "gokwik_country_code"
    ];
    for (var i = 0; i < exactKeys.length; i++) {
      var exactCountry = extractCountryCodeValue(
        getStorageValueByKey(exactKeys[i]),
        0
      );
      if (exactCountry) return exactCountry;
    }
    var found = "";
    readStorageCandidateValues(function (key, rawValue) {
      if (found) return;
      found = extractCountryCodeFromStorageCandidate(key, rawValue);
    });
    if (found) return found;
    var tokenPayload = decodeJwtPayload(getKwikpassTokenValue());
    return extractCountryCodeValue(tokenPayload, 0);
  }

  /*
    Finds a phone number inside structured Kwikpass values or token payloads.
  */
  function getKwikpassStructuredPhoneNumber() {
    var found = "";
    var tokenPayload = decodeJwtPayload(getKwikpassTokenValue());
    if (tokenPayload) {
      found = normalizeKwikpassPhoneNumber(tokenPayload);
      if (found) return found;
    }
    readStorageCandidateValues(function (key, rawValue) {
      if (found) return;
      var parsed = parseJsonString(rawValue);
      if (parsed) {
        found = normalizeKwikpassPhoneNumber(parsed);
        if (found) return;
      }
      var tokenPayloadInner = decodeJwtPayload(rawValue);
      if (tokenPayloadInner) {
        found = normalizeKwikpassPhoneNumber(tokenPayloadInner);
      }
    });
    return found;
  }

  /*
    Public phone-number resolver for the login gate. It combines country code
    and phone signals when both are available and falls back gracefully.
  */
  function getKwikpassPhoneNumber() {
    try {
      var storedPhone = getStorageValueByKey("notify_phone_number");
      var countryCode = getKwikpassCountryCodeFromStorage();
      if (countryCode && storedPhone) {
        var combinedPhone = combineCountryCodeWithPhone(
          countryCode,
          storedPhone
        );
        if (combinedPhone) return combinedPhone;
      }
      var structuredPhone = getKwikpassStructuredPhoneNumber();
      if (structuredPhone) {
        return countryCode
          ? combineCountryCodeWithPhone(countryCode, structuredPhone)
          : structuredPhone;
      }
      return normalizeKwikpassPhoneNumber(storedPhone);
    } catch (error) {
      return "";
    }
  }

  /*
    Creates a Boltic login record once per completed analysis. Duplicate and
    concurrent submissions are blocked by the synced/in-flight flags.
  */
  function createBolticLoginRecordWithPhone(phoneNumber, rawSdkPayload) {
    var phone = normalizeKwikpassPhoneNumber(phoneNumber);
    if (!phone || bolticLoginRecordSynced || bolticLoginRecordSyncInFlight) {
      return;
    }
    bolticLoginRecordSyncInFlight = true;
    fetch(BOLTIC_RECORDS_PROXY_URL, {
      method: "POST",
      headers: {
        accept: "application/json, text/plain, */*",
        "content-type": "application/json"
      },
      body: JSON.stringify({
        email: "",
        phone_number: phone,
        user_name: "",
        meta: buildBolticSkinAnalysisMeta(rawSdkPayload)
      }),
      credentials: "omit",
      mode: "cors"
    })
      .then(function (response) {
        return response.text().then(function (text) {
          var body = {};
          if (text) {
            try {
              body = JSON.parse(text);
            } catch (parseError) {
              body = { raw: text };
            }
          }
          if (!response.ok) {
            dispatchCustomEvent("skin:boltic-login-record-error", {
              status: response.status,
              body: body
            });
            return;
          }
          bolticLoginRecordSynced = true;
          dispatchCustomEvent("skin:boltic-login-record-success", {
            phone_number: phone,
            record: body
          });
        });
      })
      .catch(function (error) {
        dispatchCustomEvent("skin:boltic-login-record-error", {
          message: error && error.message ? error.message : String(error)
        });
      })
      .then(function () {
        bolticLoginRecordSyncInFlight = false;
      });
  }

  /*
    Attempts Boltic sync after login. Phone details may appear a few hundred
    milliseconds after Kwikpass reports success, so this retries briefly before
    emitting a missing-phone diagnostic event.
  */
  function syncBolticLoginRecordAfterLogin(
    rawSdkPayload,
    preferredPhoneNumber
  ) {
    var attempts = 0;
    var preferredPhone = normalizeKwikpassPhoneNumber(preferredPhoneNumber);
    function trySync() {
      if (bolticLoginRecordSynced || bolticLoginRecordSyncInFlight) return;
      var phone = preferredPhone || getKwikpassPhoneNumber();
      if (phone) {
        createBolticLoginRecordWithPhone(phone, rawSdkPayload);
        return;
      }
      attempts += 1;
      if (attempts < 14) {
        window.setTimeout(trySync, 350);
        return;
      }
      dispatchCustomEvent("skin:boltic-login-record-error", {
        message: "missing-phone-number"
      });
    }
    trySync();
  }

  /*
    Kwikpass and Shopify login detection.

    Deconstruct uses Kwikpass/Gokwik signals, Shopify customer metadata, local
    storage, session storage, and cookies to infer whether the customer is
    authenticated. The extraction helpers are intentionally tolerant because
    storefront login state can be represented differently across devices,
    widgets, and embedded Appbrew contexts.
  */
  /*
    Checks native Shopify/customer signals first. These are useful when the
    customer is already logged in before opening Skin Analysis.
  */
  function hasLoggedInCustomerSignal() {
    try {
      if (typeof getCustomerId === "function") {
        var cid = getCustomerId();
        if (
          cid != null &&
          String(cid).trim().length > 0 &&
          String(cid).trim() !== "0"
        ) {
          return true;
        }
      }
    } catch (e1) {}
    try {
      var shopCust = window.Shopify && window.Shopify.customer;
      if (shopCust && shopCust.id) return true;
    } catch (e2) {}
    try {
      var metaRoot = window.meta;
      if (metaRoot && metaRoot.customer) {
        if (metaRoot.customer.id || metaRoot.customer.email) return true;
      }
    } catch (e3) {}
    try {
      var saMeta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (saMeta && saMeta.page && saMeta.page.customerId) return true;
    } catch (e4) {}
    try {
      if (
        document.body &&
        document.body.classList.contains("customer-logged-in")
      ) {
        return true;
      }
      if (
        document.documentElement &&
        document.documentElement.classList.contains("customer-logged-in")
      ) {
        return true;
      }
    } catch (e5) {}
    try {
      var loggedMeta = document.querySelector(
        'meta[name="logged-in"][content="true"],meta[name="customer-logged-in"][content="true"]'
      );
      if (loggedMeta) return true;
    } catch (e6) {}
    return false;
  }

  /*
    A Kwikpass token is the strongest widget-level signal that login completed.
  */
  function hasKwikpassLoginSignal() {
    return !!getKwikpassTokenValue();
  }

  /*
    Final login-state decision used by the result gate.

    If the Kwikpass helpers are missing, the function returns true to avoid
    blocking customers on storefronts where the login widget is unavailable or
    intentionally disabled.
  */
  function isKwikpassFullyLoggedIn() {
    try {
      var merchantInfo = window.merchantInfo;
      if (!merchantInfo || merchantInfo.environment == null) return true;
      if (
        typeof XGokwikCoreToken !== "function" ||
        typeof getValueFromCookiesOrLocalStorage !== "function"
      ) {
        return true;
      }
      var token = getValueFromCookiesOrLocalStorage(
        XGokwikCoreToken(merchantInfo.environment)
      );
      if (!token) return hasLoggedInCustomerSignal();
      return hasKwikpassLoginSignal();
    } catch (error) {
      return true;
    }
  }

  /*
    Result login gate.

    When the SDK emits a completed skin-analysis result for an anonymous
    customer, the result screen remains blocked while the Kwikpass login popup
    is opened. The gate unlocks only after login state is detected, then the
    recommendation CTA is shown and the Boltic record sync is started.
  */
  /*
    Prevents background page scrolling while the Kwikpass login popup is active
    and the skin-analysis result is being held.
  */
  function setSkinAnalysisLoginGateScrollLock(on) {
    var cn = "cg-skin-login-gate-active";
    if (!document.body || !document.documentElement) return;
    if (on) {
      document.documentElement.classList.add(cn);
      document.body.classList.add(cn);
    } else {
      document.documentElement.classList.remove(cn);
      document.body.classList.remove(cn);
    }
  }

  /*
    Resets popup observation state for each new login-gate attempt.
  */
  function resetSkinAnalysisLoginPopupState() {
    skinAnalysisLoginPopupObserved = false;
    skinAnalysisLoginPopupMissingTicks = 0;
    if (skinAnalysisLoginOpenTimerId != null) {
      window.clearTimeout(skinAnalysisLoginOpenTimerId);
      skinAnalysisLoginOpenTimerId = null;
    }
  }

  /*
    Stops the class observer used to detect login-related body/html changes.
  */
  function stopSkinAnalysisBodyClassObserver() {
    if (skinAnalysisLoginBodyObserver) {
      skinAnalysisLoginBodyObserver.disconnect();
      skinAnalysisLoginBodyObserver = null;
    }
  }

  /*
    Watches html/body class changes because Kwikpass and Shopify login widgets
    often indicate login transitions by toggling page-level classes.
  */
  function startSkinAnalysisBodyClassObserver() {
    stopSkinAnalysisBodyClassObserver();
    if (!window.MutationObserver || !document.body) return;
    var mo = new MutationObserver(function () {
      tryUnlockSkinAnalysisIfLoggedIn();
    });
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    });
    mo.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"]
    });
    skinAnalysisLoginBodyObserver = mo;
  }

  /*
    Hooks fetch and XMLHttpRequest once to re-check login state after network
    calls that commonly follow login or cart/customer updates.

    The hook is intentionally narrow and only schedules unlock checks while the
    Skin Analysis login gate is active.
  */
  function ensureSkinAnalysisNetworkUnlockHooks() {
    if (window.__cgDeconstructSkinNetworkUnlockHooked) return;
    window.__cgDeconstructSkinNetworkUnlockHooked = true;
    var nf = window.fetch;
    if (typeof nf === "function") {
      try {
        window.fetch = function () {
          var args = arguments;
          return nf.apply(window, args).then(function (response) {
            try {
              if (
                skinAnalysisLoginGateActive &&
                response &&
                response.ok &&
                typeof args[0] === "string"
              ) {
                var u = args[0];
                if (
                  u.indexOf("/cart") !== -1 ||
                  u.indexOf("cart.js") !== -1 ||
                  u.indexOf("update.js") !== -1 ||
                  u.indexOf("customer") !== -1
                ) {
                  window.setTimeout(tryUnlockSkinAnalysisIfLoggedIn, 350);
                  window.setTimeout(tryUnlockSkinAnalysisIfLoggedIn, 900);
                }
              }
            } catch (eFetch) {}
            return response;
          });
        };
      } catch (fetchHookError) {}
    }
    if (typeof XMLHttpRequest !== "undefined" && XMLHttpRequest.prototype) {
      try {
        var xopen = XMLHttpRequest.prototype.open;
        var xsend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function (method, url) {
          try {
            this.__cgSkinUrl = typeof url === "string" ? url : "";
          } catch (eO) {
            this.__cgSkinUrl = "";
          }
          return xopen.apply(this, arguments);
        };
        XMLHttpRequest.prototype.send = function () {
          var xhr = this;
          xhr.addEventListener(
            "load",
            function () {
              try {
                if (
                  !skinAnalysisLoginGateActive ||
                  xhr.status < 200 ||
                  xhr.status >= 300
                ) {
                  return;
                }
                var u = xhr.__cgSkinUrl || "";
                if (
                  u.indexOf("/cart") !== -1 ||
                  u.indexOf("cart.js") !== -1 ||
                  u.indexOf("update.js") !== -1 ||
                  u.indexOf("customer") !== -1
                ) {
                  window.setTimeout(tryUnlockSkinAnalysisIfLoggedIn, 350);
                  window.setTimeout(tryUnlockSkinAnalysisIfLoggedIn, 900);
                }
              } catch (eX) {}
            },
            false
          );
          return xsend.apply(this, arguments);
        };
      } catch (xhrHookError) {}
    }
  }

  /*
    Stops all polling/observer-based unlock checks when login succeeds or the
    customer abandons the login popup.
  */
  function stopSkinAnalysisLoginUnlockWatch() {
    if (skinAnalysisLoginPollId != null) {
      window.clearInterval(skinAnalysisLoginPollId);
      skinAnalysisLoginPollId = null;
    }
    stopSkinAnalysisBodyClassObserver();
  }

  /*
    Visibility guard for popup detection. It filters out hidden template nodes,
    invisible triggers, and off-screen or collapsed elements.
  */
  function isVisibleElement(node) {
    if (!node || node.nodeType !== 1) return false;
    var style = window.getComputedStyle ? window.getComputedStyle(node) : null;
    if (
      style &&
      (style.display === "none" ||
        style.visibility === "hidden" ||
        style.pointerEvents === "none")
    ) {
      return false;
    }
    if (style) {
      var opacity = parseFloat(style.opacity);
      if (Number.isFinite(opacity) && opacity < 0.05) return false;
    }
    var rect = node.getBoundingClientRect && node.getBoundingClientRect();
    if (!rect) return false;
    return rect.width > 8 && rect.height > 8;
  }

  /*
    Identifies the header/login trigger buttons so popup detection does not
    mistake the button itself for an open modal.
  */
  function isKwikpassLoginTriggerNode(node) {
    if (!node || node.nodeType !== 1) return false;
    var id = String(node.id || "");
    return (
      id === "kp-login-button-header-logo-mobile" ||
      id === "kp-login-button-header-logo" ||
      id === "kp-login-button-header" ||
      id === "gokwik-login-button"
    );
  }

  /*
    Heuristic detector for Kwikpass/Gokwik modal, iframe, overlay, and wrapper
    nodes. This is intentionally broad because widget markup can differ between
    versions.
  */
  function isKwikpassPopupCandidate(node) {
    if (!node || node.nodeType !== 1 || isKwikpassLoginTriggerNode(node)) {
      return false;
    }
    var tag = String(node.tagName || "").toUpperCase();
    if (tag === "BUTTON" || tag === "A") return false;
    var text = (
      String(node.id || "") +
      " " +
      String(node.className || "") +
      " " +
      String(node.getAttribute("src") || "") +
      " " +
      String(node.getAttribute("name") || "") +
      " " +
      String(node.getAttribute("aria-label") || "")
    ).toLowerCase();
    if (!/(kwik|gokwik|kp)/.test(text)) return false;
    if (tag === "IFRAME") return true;
    return /(modal|popup|iframe|overlay|login|sso|auth|drawer|container|wrapper)/.test(
      text
    );
  }

  /*
    Scans visible DOM nodes to determine whether the login popup is currently
    open.
  */
  function isKwikpassLoginPopupVisible() {
    var nodes = document.querySelectorAll("iframe,[id],[class]");
    for (var i = 0; i < nodes.length; i++) {
      if (isKwikpassPopupCandidate(nodes[i]) && isVisibleElement(nodes[i])) {
        return true;
      }
    }
    return false;
  }

  /*
    Tracks whether the popup appeared and then disappeared. If it disappears
    without login, the customer likely closed it, so the Skin Analysis result
    gate is abandoned and the page returns to the starting state.
  */
  function updateSkinAnalysisLoginPopupState() {
    if (!skinAnalysisLoginGateActive) return;
    if (isKwikpassFullyLoggedIn()) {
      unlockSkinAnalysisAfterLogin();
      return;
    }
    var popupVisible = isKwikpassLoginPopupVisible();
    if (popupVisible) {
      skinAnalysisLoginPopupObserved = true;
      skinAnalysisLoginPopupMissingTicks = 0;
      return;
    }
    if (!skinAnalysisLoginPopupObserved) return;
    skinAnalysisLoginPopupMissingTicks += 1;
    if (
      skinAnalysisLoginPopupMissingTicks >=
      SKIN_LOGIN_POPUP_MISSING_TICKS_TO_ABANDON
    ) {
      abandonSkinAnalysisLoginGateToHome();
    }
  }

  /*
    Shared unlock check used by timers, observers, focus events, and network
    hooks.
  */
  function tryUnlockSkinAnalysisIfLoggedIn() {
    if (!skinAnalysisLoginGateActive) return;
    if (isKwikpassFullyLoggedIn()) {
      unlockSkinAnalysisAfterLogin();
      return;
    }
    updateSkinAnalysisLoginPopupState();
  }

  /*
    Starts all login-gate unlock mechanisms: network hooks, class observer, and
    fast polling. The polling interval is short because the gate is active only
    during the login popup interaction.
  */
  function startSkinAnalysisLoginUnlockWatch() {
    stopSkinAnalysisLoginUnlockWatch();
    ensureSkinAnalysisNetworkUnlockHooks();
    startSkinAnalysisBodyClassObserver();
    skinAnalysisLoginPollId = window.setInterval(function () {
      tryUnlockSkinAnalysisIfLoggedIn();
    }, SKIN_LOGIN_POPUP_POLL_INTERVAL_MS);
  }

  /*
    Attempts to click the visible Kwikpass login trigger. A second pass allows
    nearly hidden but still actionable buttons used by some responsive headers.
  */
  function clickVisibleKwikpassLoginTrigger() {
    var ids = [
      "kp-login-button-header-logo-mobile",
      "kp-login-button-header-logo",
      "kp-login-button-header",
      "gokwik-login-button"
    ];
    for (var pass = 0; pass < 2; pass++) {
      for (var i = 0; i < ids.length; i++) {
        var el = document.getElementById(ids[i]);
        if (!el || typeof el.click !== "function") continue;
        if (pass === 0) {
          var st = window.getComputedStyle(el);
          if (
            st.display === "none" ||
            st.visibility === "hidden" ||
            st.pointerEvents === "none"
          ) {
            continue;
          }
          var op = parseFloat(st.opacity);
          if (Number.isFinite(op) && op < 0.05) continue;
          var rect = el.getBoundingClientRect();
          if (rect.width < 2 || rect.height < 2) continue;
        } else {
          var st2 = window.getComputedStyle(el);
          if (st2.display === "none") continue;
        }
        el.click();
        return true;
      }
    }
    return false;
  }

  /*
    Opens the Kwikpass login flow using the best available storefront API. The
    direct trigger click is preferred, followed by known Kwikpass helper
    functions exposed by the theme/widget.
  */
  function openKwikpassLoginForSkinAnalysis() {
    skinAnalysisLoginPopupMissingTicks = 0;
    if (clickVisibleKwikpassLoginTrigger()) return true;
    try {
      if (typeof handleKpAndShopifyLogin === "function") {
        handleKpAndShopifyLogin(
          (window.location.pathname || "/") + (window.location.search || "")
        );
        return true;
      }
      if (typeof window.handleKpAndShopifyLogin === "function") {
        window.handleKpAndShopifyLogin(
          (window.location.pathname || "/") + (window.location.search || "")
        );
        return true;
      }
    } catch (e1) {}
    try {
      if (typeof openIframe === "function") {
        openIframe("kp_sso_token");
        return true;
      }
      if (typeof window.openIframe === "function") {
        window.openIframe("kp_sso_token");
        return true;
      }
    } catch (e2) {}
    return false;
  }

  /*
    Defers opening the popup slightly so the SDK result event stack can finish
    and the scroll lock/body state can settle.
  */
  function openKwikpassLoginForSkinAnalysisSoon() {
    if (skinAnalysisLoginOpenTimerId != null) {
      window.clearTimeout(skinAnalysisLoginOpenTimerId);
    }
    skinAnalysisLoginOpenTimerId = window.setTimeout(function () {
      skinAnalysisLoginOpenTimerId = null;
      if (!skinAnalysisLoginGateActive || isKwikpassFullyLoggedIn()) return;
      if (!openKwikpassLoginForSkinAnalysis()) {
        abandonSkinAnalysisLoginGateToHome();
        return;
      }
      window.setTimeout(updateSkinAnalysisLoginPopupState, 250);
    }, 120);
  }

  /*
    Clears all state associated with an active login gate.
  */
  function clearSkinAnalysisLoginGateState() {
    skinAnalysisLoginGateActive = false;
    pendingSkinAnalysisPayloadForLogin = null;
    stopSkinAnalysisLoginUnlockWatch();
    resetSkinAnalysisLoginPopupState();
    setSkinAnalysisLoginGateScrollLock(false);
  }

  /*
    Returns the experience to its initial state after the login popup is closed
    without a confirmed login.
  */
  function resetSkinAnalysisToStartScreen() {
    try {
      if (window.GlamAR && typeof window.GlamAR.close === "function") {
        window.GlamAR.close();
      }
    } catch (error) {}
    latestSkinProfile = null;
    latestRecommendationProducts = [];
    routineCartDataCache = null;
    bolticLoginRecordSynced = false;
    bolticLoginRecordSyncInFlight = false;
    currentRoutinePeriod = "am";
    analysisStarted = false;
    var mount = getRecommendationMount();
    if (mount) mount.innerHTML = "";
    if (document.body) {
      document.body.classList.remove("cg-deconstruct-reco-active");
    }
    setCgLoading(false);
    try {
      var wrapper = $("cg-inline-wrapper");
      if (wrapper && typeof wrapper.scrollIntoView === "function") {
        wrapper.scrollIntoView({ block: "start" });
      }
    } catch (scrollError) {}
  }

  /*
    Handles login-gate abandonment by clearing gate state and resetting the SDK
    result/recommendation UI.
  */
  function abandonSkinAnalysisLoginGateToHome() {
    clearSkinAnalysisLoginGateState();
    resetSkinAnalysisToStartScreen();
  }

  /*
    Completes the held result flow after login succeeds: show CTA, expose the
    latest result for diagnostics, emit the integration event, and sync Boltic.
  */
  function unlockSkinAnalysisAfterLogin() {
    if (!skinAnalysisLoginGateActive) return;
    var payload = pendingSkinAnalysisPayloadForLogin;
    var phone = getKwikpassPhoneNumber();
    clearSkinAnalysisLoginGateState();
    showRecommendationCTA();
    dispatchCustomEvent("skin:result", {
      profile: latestSkinProfile,
      payload: payload
    });
    window.__CG_LATEST_SKIN_ANALYSIS = {
      profile: latestSkinProfile,
      payload: payload
    };
    //keeping it disabled for now
    //    syncBolticLoginRecordAfterLogin(payload, phone);
  }

  /*
    Binds one-time global listeners that can indicate login completion after
    Kwikpass updates the page.
  */
  function ensureKwikpassLoginGateListener() {
    if (kwikpassLoginListenerBound) return;
    kwikpassLoginListenerBound = true;
    window.addEventListener("update-dom", function () {
      window.setTimeout(tryUnlockSkinAnalysisIfLoggedIn, 200);
    });
    window.addEventListener(
      "focus",
      function () {
        window.setTimeout(function () {
          tryUnlockSkinAnalysisIfLoggedIn();
          updateSkinAnalysisLoginPopupState();
        }, 120);
      },
      true
    );
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState !== "visible") return;
      window.setTimeout(function () {
        tryUnlockSkinAnalysisIfLoggedIn();
        updateSkinAnalysisLoginPopupState();
      }, 120);
    });
  }

  /*
    Starts the result gate for anonymous customers. The raw SDK payload is held
    in memory until login succeeds so no analysis data is lost.
  */
  function startSkinAnalysisLoginGate(rawSdkPayload) {
    if (skinAnalysisLoginGateActive) return true;
    skinAnalysisLoginGateActive = true;
    pendingSkinAnalysisPayloadForLogin = rawSdkPayload;
    resetSkinAnalysisLoginPopupState();
    setSkinAnalysisLoginGateScrollLock(true);
    ensureKwikpassLoginGateListener();
    startSkinAnalysisLoginUnlockWatch();
    openKwikpassLoginForSkinAnalysisSoon();
    return true;
  }

  /*
    Determines whether an SDK event represents a completed skin-analysis result.
    Completed results update the cached skin profile, pass through the login
    gate when required, then expose the recommendation CTA.
  */
  function handlePossibleSkinResult(eventData) {
    var option = getSkinAnalysisOption(eventData);
    var value = getSkinAnalysisValue(eventData);
    var profile = getSkinRecommendationProfile(value);
    if (option !== "result" && !profile) return false;
    latestSkinProfile = profile ||
      latestSkinProfile || {
        skinType: "combination",
        concerns: ["acne", "pores", "wrinkles"]
      };
    if (!isKwikpassFullyLoggedIn()) {
      return startSkinAnalysisLoginGate(eventData);
    }
    showRecommendationCTA();
    //keeping it disabled for now
    //syncBolticLoginRecordAfterLogin(eventData);
    dispatchCustomEvent("skin:result", {
      profile: latestSkinProfile,
      payload: eventData
    });
    window.__CG_LATEST_SKIN_ANALYSIS = {
      profile: latestSkinProfile,
      payload: eventData
    };
    return true;
  }

  /*
    Attaches all GlamAR SDK listeners after init() has completed. The wrapper
    can recreate its internal emitter during initialization, so binding here
    keeps readiness, result, cart, and diagnostic handlers attached to the
    active SDK instance.
  */
  function bindSDKEvents() {
    console.log("[CG] >>> CHECKPOINT 9: bindSDKEvents starting");
    var GA = window.GlamAR;
    if (!GA) {
      console.error("[CG] !!! GlamAR missing in bindSDKEvents");
      return;
    }
    if (!GA.addEventListener) {
      console.error(
        "[CG] !!! GlamAR.addEventListener missing! Available keys:",
        Object.keys(GA)
      );
      return;
    }
    console.log("[CG] >>> GlamAR API surface:", Object.keys(GA));

    /*
      Primary readiness listener. If the SDK was already loaded by the home
      page preload bridge, isLoaded() immediately marks this instance ready.
    */
    GA.addEventListener("loaded", function () {
      console.log("[CG] >>> EVENT: loaded");
      markSDKReady();
    });
    if (isGlamARLoaded()) markSDKReady();

    /*
      Main skin-analysis event listener.

      This receives both analysis progress and result payloads. Result-like
      payloads are sent through the login gate, while recommendation payloads
      are filtered to one product per configured GlamAR product type.
    */
    try {
      GA.addEventListener("skin-analysis", function (payload) {
        console.log("[CG] >>> EVENT: skin-analysis | payload:", payload);
        handlePossibleSkinResult(payload);
        var option = payload && (payload.options || payload.option);
        var value = payload && payload.value;
        var productList = null;
        if (value && Array.isArray(value.product_list))
          productList = value.product_list;
        else if (value && Array.isArray(value.products))
          productList = value.products;
        else if (payload && Array.isArray(payload.product_list))
          productList = payload.product_list;
        else if (payload && Array.isArray(payload.products))
          productList = payload.products;
        var optStr = String(option || "").toLowerCase();
        var looksLikeRecommendation =
          optStr.indexOf("recomm") !== -1 ||
          (productList && productList.length > 0);
        if (looksLikeRecommendation && productList) {
          console.log(
            "[CG] >>> RECOMMENDATION DETECTED, filtering... option:",
            option,
            "list size:",
            productList.length
          );
          try {
            var original = productList.slice();
            var filtered = pickOnePerType(original);
            productList.length = 0;
            Array.prototype.push.apply(productList, filtered);
            console.log(
              "[CG] >>> Filtered list now has",
              productList.length,
              "items"
            );
          } catch (e) {
            console.error("[CG] !!! filter error:", e);
          }
        }
        setCgLoading(false);
        if (!analysisStarted) analysisStarted = true;
      });
      console.log("[CG] >>> CHECKPOINT 10: skin-analysis listener attached");
    } catch (e) {
      console.error("[CG] !!! skin-analysis bind error:", e);
    }

    /*
      Secondary SDK listeners.

      These cover cart requests, top-navigation cart clicks, and alternate
      result events emitted by different SDK versions. The catch-all logging is
      retained intentionally for client-side diagnosis during storefront QA.
    */
    [
      "ready",
      "initialized",
      "render",
      "mount",
      "unmount",
      "resize",
      "closed",
      "error",
      "analysis-complete",
      "analysis-started",
      "photo-taken",
      "photo-uploaded",
      "recommendation",
      "recommendations",
      "product-recommendation",
      "camera-opened",
      "camera-closed",
      "camera-access-issue",
      "add-to-cart",
      "ui-interaction"
    ].forEach(function (evt) {
      try {
        GA.addEventListener(evt, function (d) {
          console.log("[CG] >>> EVENT:", evt, "| data:", d);
          if (evt === "analysis-complete") handlePossibleSkinResult(d);
          if (evt === "add-to-cart") addToCart(d);
          if (
            evt === "ui-interaction" &&
            d &&
            d.screenType === "topNavigationPanel" &&
            d.buttonType === "globalCart"
          )
            openCart();
        });
      } catch (e) {}
    });
    console.log("[CG] >>> CHECKPOINT 11: all event listeners attached");
  }

  /*
    Resolves a product handle from a variant ID for Appbrew add-to-cart calls.

    Appbrew requires both a product handle and a Shopify ProductVariant GID.
    The lookup tries the fastest Shopify endpoints first, then falls back to
    paginated product scans and search suggestions for older storefront setups.
  */
  function fetchProductHandleByVariantId(vid, productName) {
    /*
      Scans a product list response for the variant ID and returns the owning
      product handle when found.
    */
    function scanProductsForVariant(products) {
      var ps = products || [];
      for (var i = 0; i < ps.length; i++) {
        var vs = ps[i].variants || [];
        for (var j = 0; j < vs.length; j++) {
          if (String(vs[j].id) === String(vid)) return ps[i].handle;
        }
      }
      return null;
    }
    /*
      Shopify's /variants/{id}.js endpoint can reveal the product ID. When
      available, it is the most direct handle lookup path.
    */
    function fetchByVariantEndpoint() {
      return fetch("/variants/" + encodeURIComponent(String(vid)) + ".js", {
        credentials: "same-origin"
      })
        .then(function (r) {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.json();
        })
        .then(function (v) {
          if (!v || !v.product_id) return null;
          return fetch(
            "/products.json?ids=" +
              encodeURIComponent(String(v.product_id)) +
              "&limit=1",
            { credentials: "same-origin" }
          )
            .then(function (r2) {
              if (!r2.ok) throw new Error("HTTP " + r2.status);
              return r2.json();
            })
            .then(function (p2) {
              var arr = (p2 && p2.products) || [];
              return (arr.length > 0 && arr[0].handle) || null;
            });
        })
        .catch(function () {
          return null;
        });
    }
    /*
      Fallback for stores where /variants/{id}.js is unavailable. The scan is
      capped to avoid excessive storefront requests.
    */
    function fetchByPaginatedProducts() {
      var page = 1;
      function next() {
        if (page > 8) return Promise.resolve(null);
        return fetch("/products.json?limit=250&page=" + page, {
          credentials: "same-origin"
        })
          .then(function (r) {
            if (!r.ok) throw new Error("HTTP " + r.status);
            return r.json();
          })
          .then(function (d) {
            var ps = (d && d.products) || [];
            if (ps.length === 0) return null;
            var found = scanProductsForVariant(ps);
            if (found) return found;
            page += 1;
            return next();
          })
          .catch(function () {
            return null;
          });
      }
      return next();
    }
    /*
      Last fallback using Shopify predictive search. Product name is used only
      after ID-based lookup attempts fail.
    */
    function fetchBySuggestQuery(query) {
      if (!query) return Promise.resolve(null);
      return fetch(
        "/search/suggest.json?q=" +
          encodeURIComponent(query) +
          "&resources[type]=product&resources[limit]=5",
        { credentials: "same-origin" }
      )
        .then(function (r) {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.json();
        })
        .then(function (d) {
          try {
            var p = d.resources.results.products;
            if (p && p.length > 0) return p[0].handle || null;
          } catch (e) {}
          return null;
        })
        .catch(function () {
          return null;
        });
    }
    return fetchByVariantEndpoint()
      .then(function (h) {
        if (h) return h;
        return fetchByPaginatedProducts();
      })
      .then(function (h) {
        if (h) return h;
        return fetchBySuggestQuery("id:" + vid);
      })
      .then(function (h) {
        if (h) return h;
        return fetchBySuggestQuery(productName || "");
      })
      .catch(function () {
        return null;
      });
  }

  /*
    SDK cart bridge.

    GlamAR add-to-cart events can reference variants using direct IDs, Shopify
    GIDs, SKUs, or product handles. The functions below normalize those inputs
    and route the cart action to Appbrew when embedded in the app, otherwise to
    Shopify Ajax Cart APIs on the web storefront.
  */
  /*
    Normalizes SDK cart event data into a small internal shape:
    skuId/variant reference, quantity, product handle, and product name.
  */
  function parseCartEventData(data) {
    if (typeof data === "string" || typeof data === "number")
      return { skuId: String(data), quantity: 1, handle: null };
    var p = data && data.payload ? data.payload : {};
    var rawSku =
      (data &&
        (data.skuId ||
          data.skuID ||
          data.variantId ||
          data.variantID ||
          data.id ||
          data.sku ||
          data.productVariantId)) ||
      (p && (p.skuId || p.variantId || p.id || p.sku || p.productVariantId)) ||
      null;
    var handle =
      (data && (data.handle || data.productHandle)) ||
      (p && (p.handle || p.productHandle)) ||
      null;
    var productName =
      (data && (data.productName || data.name || data.title)) ||
      (p && (p.productName || p.name || p.title)) ||
      null;
    var quantity = Number((data && data.quantity) || (p && p.quantity) || 1);
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
    return {
      skuId: rawSku ? String(rawSku) : null,
      quantity: quantity,
      handle: handle ? String(handle) : null,
      productName: productName ? String(productName) : null
    };
  }

  /*
    Converts a variant reference into both a numeric Shopify variant ID and the
    ProductVariant GID format expected by Appbrew.
  */
  function normalizeVariantRef(rawSku) {
    if (!rawSku) return { variantId: null, gid: null };
    var raw = String(rawSku).trim();
    if (!raw) return { variantId: null, gid: null };
    if (raw.indexOf("gid://shopify/ProductVariant/") === 0) {
      return { variantId: raw.split("/").pop(), gid: raw };
    }
    var base = raw.indexOf("_") > -1 ? raw.split("_")[0] : raw;
    var parts = base.split("/");
    var tail = parts[parts.length - 1];
    return { variantId: tail, gid: "gid://shopify/ProductVariant/" + tail };
  }

  /*
    Sends success/failure feedback back to the GlamAR wrapper so SDK UI states
    stay synchronized with Shopify/Appbrew cart results.
  */
  function notifyGlamARCartResult(skuId, success, errorMessage) {
    if (!window.GlamAR || !window.GlamAR.addedToCart) return;
    window.GlamAR.addedToCart(
      skuId,
      success
        ? { success: true }
        : {
            success: false,
            error: errorMessage || "Failed"
          }
    );
  }

  /*
    Fetches and caches the Shopify cart. Webview/Appbrew cart state is managed
    externally, so this is skipped inside the app context.
  */
  function fetchCartData() {
    if (isAppBrew()) return Promise.resolve(null);
    return fetch("/cart.json")
      .then(function (response) {
        if (!response.ok) throw new Error("cart-fetch-failed");
        return response.json();
      })
      .then(function (cartData) {
        routineCartDataCache = cartData;
        return cartData;
      });
  }

  /*
    Returns cached cart data when possible, otherwise fetches the current cart.
  */
  function getCartDataForQuietUpdate() {
    if (routineCartDataCache) return Promise.resolve(routineCartDataCache);
    return fetchCartData();
  }

  /*
    Counts how many units of a variant are already in the cart so quiet cart
    updates can increment rather than overwrite quantities.
  */
  function getCartVariantQuantity(cartData, variantId) {
    var target = String(variantId || "");
    var items = cartData && Array.isArray(cartData.items) ? cartData.items : [];
    var quantity = 0;
    for (var i = 0; i < items.length; i++) {
      var itemId = String(items[i].id || items[i].variant_id || "");
      if (itemId === target) quantity += Number(items[i].quantity) || 0;
    }
    return quantity;
  }

  /*
    Updates Shopify cart quantities through /cart/update.js and refreshes the
    local cart cache with the response.
  */
  function updateCartQuantities(updates) {
    if (isAppBrew()) return Promise.resolve(null);
    return fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (errorData) {
          throw new Error(errorData.description || "Failed to update cart");
        });
      }
      return response.json().then(function (cartData) {
        routineCartDataCache = cartData;
        return cartData;
      });
    });
  }

  /*
    Posts a single add-to-cart request using the EXACT shape the theme's own
    product form / <add-to-cart> element uses (global.js).

    This store wraps window.fetch with a custom cart layer (GoKwik / Whatmore).
    That wrapper keys on the theme's native add request:
      - URL    : window.routes.cart_add_url  ("/cart/add", not "/cart/add.js")
      - Accept : application/javascript
      - header : X-Requested-With: XMLHttpRequest
      - body   : JSON including sections_url
    Posting raw "/cart/add.js" with Accept: application/json bypassed that
    contract and the wrapper rejected it with a 422, so nothing was added.
    Mirroring the theme keeps the wrapper happy.

    A failed add returns HTTP 422 whose JSON body carries a `status` field, so
    we inspect the parsed body (not just response.ok) to detect rejections.
  */
  /*
    Line item property stamped on products added from the skin-analysis regimen
    screen (the "Add full regimen" button). The leading underscore keeps it
    hidden from the storefront cart/checkout UI while still recording it on the
    order in the Shopify admin, so the team can recognize orders that came from
    the GlamAR skin analysis flow.
  */
  var GLAMAR_LINE_ITEM_PROPERTIES = {
    _source: "GlamAR Skin Analysis"
  };

  function postCartAddRequest(body) {
    var url =
      (window.routes && window.routes.cart_add_url) || "/cart/add";
    body = body || {};

    /*
      Use multipart FormData (not JSON) and flatten line item properties to
      "properties[key]" fields. This matches the theme's own product form
      (ProductForm.onSubmitHandler in global.js), which is the only add shape
      the GoKwik cart layer reliably forwards WITH line item properties â€” a
      nested JSON `properties` object gets stripped by the GoKwik fetch wrapper.

      Content-Type is intentionally omitted so the browser sets the correct
      multipart boundary automatically (the theme deletes it for the same
      reason).
    */
    var formData = new FormData();
    if (body.id != null) formData.append("id", body.id);
    formData.append("quantity", body.quantity != null ? body.quantity : 1);
    if (body.properties && typeof body.properties === "object") {
      Object.keys(body.properties).forEach(function (key) {
        formData.append("properties[" + key + "]", body.properties[key]);
      });
    }
    if (body.sections_url) formData.append("sections_url", body.sections_url);

    return fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: formData
    })
      .then(function (response) {
        return response.json().catch(function () {
          return null;
        });
      })
      .then(function (state) {
        if (!state || state.status) {
          throw new Error(
            (state && (state.description || state.message)) ||
              "Failed to add to cart"
          );
        }
        return state;
      });
  }

  /*
    Adds a single variant without opening the cart drawer. Used for individual
    recommendation cards and (sequentially) for the full-regimen bulk add.
  */
  function addCartVariantQuietly(variantId, quantity) {
    var safeQuantity = Number(quantity) || 1;
    var id = String(variantId || "").trim();
    if (!id) return Promise.resolve(false);
    if (isAppBrew()) return Promise.resolve(false);
    return postCartAddRequest({
      id: id,
      quantity: safeQuantity,
      sections_url: window.location.pathname
    })
      .then(function () {
        return fetchCartData().then(function (cartData) {
          updateCartBadge(cartData && cartData.item_count, cartData);
          return true;
        });
      })
      .catch(function () {
        return false;
      });
  }

  /*
    Resolves a recommendation product to a variant ID, then quietly adds one
    unit to the cart.
  */
  function addProductQuietly(product) {
    if (!product) return Promise.resolve(false);
    return resolveProductCartVariantId(product)
      .then(function (variantId) {
        if (!variantId) return false;
        return addCartVariantQuietly(variantId, 1);
      })
      .catch(function () {
        return false;
      });
  }

  /*
    Adds multiple routine products to the cart.

    Two store-specific constraints drive this implementation:

    1. theme.liquid wraps fetch/XHR with a bot guard that BLOCKS any
       /cart/add(.js) request made more than 4 seconds after the last trusted
       user gesture (and any request while the tab is hidden). So every add
       must fire quickly after the button click.
    2. The classic batch endpoint ({ items: [...] }) is atomic: if a single
       variant is unavailable the whole request fails (the 422 the user saw)
       and nothing is added.

    So we:
      - Resolve every variant first. Variants are already warmed by
        preloadProductCartContext, so this is effectively instant (cached).
      - Add them sequentially via postCartAddRequest WITHOUT a cart fetch
        between items. Skipping the per-item cart fetch keeps the whole loop
        comfortably inside the 4s trusted-action window, while sequential
        (not parallel) requests avoid cart-token race conditions.
      - Refresh the cart once at the end.
    Each add is independent, so one unavailable product no longer blocks the
    rest, and results map 1:1 to the input list.
  */
  function addRoutineProductsQuietly(products) {
    var list = Array.isArray(products) ? products : [];

    if (isAppBrew()) {
      return Promise.resolve({
        success: false,
        results: list.map(function () { return false; }),
        variantIds: []
      });
    }

    return Promise.all(
      list.map(function (product) {
        return resolveProductCartVariantId(product).catch(function () {
          return "";
        });
      })
    )
      .then(function (variantIds) {
        var results = [];

        var chain = variantIds.reduce(function (promise, variantId) {
          return promise.then(function () {
            var id = String(variantId || "").trim();
            if (!id) {
              results.push(false);
              return;
            }
            return postCartAddRequest({
              id: id,
              quantity: 1,
              properties: GLAMAR_LINE_ITEM_PROPERTIES,
              sections_url: window.location.pathname
            })
              .then(function () {
                results.push(true);
              })
              .catch(function () {
                results.push(false);
              });
          });
        }, Promise.resolve());

        return chain.then(function () {
          return fetchCartData()
            .then(function (nextCartData) {
              updateCartBadge(
                nextCartData && nextCartData.item_count,
                nextCartData
              );
              return {
                success:
                  results.length > 0 &&
                  results.every(function (s) { return !!s; }),
                results: results,
                variantIds: variantIds,
                cartData: nextCartData
              };
            })
            .catch(function () {
              return {
                success:
                  results.length > 0 &&
                  results.every(function (s) { return !!s; }),
                results: results,
                variantIds: variantIds
              };
            });
        });
      })
      .catch(function () {
        return {
          success: false,
          results: list.map(function () { return false; }),
          variantIds: []
        };
      });
  }

  /*
    Refreshes only cart data and badge counts. This is used when opening or
    replacing the full cart drawer is unnecessary.
  */
  function refreshCartBadgeOnly() {
    if (isAppBrew()) return Promise.resolve(null);
    return fetchCartData().then(function (cd) {
      updateCartBadge(cd.item_count);
      return cd;
    });
  }

  /*
    Handles add-to-cart requests emitted directly by the GlamAR SDK.

    Webview/Appbrew: resolve product handle and call appbrewSDK.addToCart.
    Browser storefront: call Shopify /cart/add.js, then refresh the cart UI.
  */
  function addToCart(eventData, options) {
    options = options || {};
    var parsed = parseCartEventData(eventData);
    if (!parsed.skuId) return Promise.resolve(false);
    var norm = normalizeVariantRef(parsed.skuId);
    var variantId = norm.variantId,
      gid = norm.gid,
      quantity = parsed.quantity;
    if (!variantId) return Promise.resolve(false);
    if (isAppBrew()) {
      if (!window.appbrewSDK || !window.appbrewSDK.addToCart)
        return Promise.resolve(false);
      var hp = parsed.handle
        ? Promise.resolve(parsed.handle)
        : fetchProductHandleByVariantId(variantId, parsed.productName);
      return hp
        .then(function (h) {
          if (!h) {
            notifyGlamARCartResult(parsed.skuId, false, "Handle not found");
            return false;
          }
          return window.appbrewSDK
            .addToCart(h, gid, quantity)
            .then(function (r) {
              if (r && r.status === "success") {
                if (window.appbrewSDK.showToast)
                  window.appbrewSDK.showToast("addToCart", "Added!");
                notifyGlamARCartResult(parsed.skuId, true);
                return true;
              }
              notifyGlamARCartResult(
                parsed.skuId,
                false,
                (r && r.message) || "Error"
              );
              return false;
            });
        })
        .catch(function (e) {
          if (window.appbrewSDK && window.appbrewSDK.showToast)
            window.appbrewSDK.showToast("error", "Failed");
          notifyGlamARCartResult(parsed.skuId, false, e.message);
          return false;
        });
    }
    return postCartAddRequest({
      id: variantId,
      quantity: quantity,
      sections_url: window.location.pathname
    })
      .then(function () {
        if (options.refreshDrawer) return refreshCartDrawer();
        if (options.updateBadge === false) return null;
        return refreshCartBadgeOnly();
      })
      .then(function () {
        notifyGlamARCartResult(parsed.skuId, true);
        return true;
      })
      .catch(function (e) {
        notifyGlamARCartResult(parsed.skuId, false, e.message);
        return false;
      });
  }

  /*
    Opens the cart in the correct environment. Appbrew navigates to the native
    cart screen, while browser storefronts use Gokwik cart APIs or theme cart
    drawer triggers before falling back to /cart.
  */
  function openCart() {
    if (isAppBrew()) {
      if (window.appbrewSDK && window.appbrewSDK.gotoLink)
        window.appbrewSDK.gotoLink({ kind: "screen", value: "cart" });
      else window.location.href = "/cart";
      return;
    }
    if (typeof window.openGokwikSideCart === "function") {
      try {
        if (typeof window.refreshSideCart === "function") {
          var refresh = window.refreshSideCart(false, true);
          if (refresh && typeof refresh.then === "function") {
            refresh
              .catch(function () {})
              .then(function () {
                try {
                  window.openGokwikSideCart();
                } catch (error) {}
              });
            return;
          }
        }
        window.openGokwikSideCart();
        return;
      } catch (error) {}
    }
    var cartIcon = document.querySelector("#cart-icon-bubble");
    var cartSummary = cartIcon && cartIcon.closest("summary");
    if (cartSummary && typeof cartSummary.click === "function") {
      cartSummary.click();
      return;
    }
    ["cart:open", "cart:toggle", "cart:show", "open-cart"].forEach(
      function (e) {
        document.dispatchEvent(new Event(e));
      }
    );
    var sels = [
      '[data-action="cart"]',
      ".bottom-cart-icon",
      "#cart-icon-bubble",
      "[data-cart-toggle]",
      '[aria-controls="cart-drawer"]',
      'button[name="cart"]',
      'a[href*="/cart"]'
    ];
    for (var i = 0; i < sels.length; i++) {
      var el = document.querySelector(sels[i]);
      if (el) {
        el.click();
        return;
      }
    }
    window.location.href = "/cart";
  }

  /*
    Refreshes the Shopify cart drawer section after cart updates so the native
    theme UI reflects the new cart contents.
  */
  function refreshCartDrawer() {
    if (isAppBrew()) return Promise.resolve();
    return fetch("/cart.json")
      .then(function (r) {
        return r.json();
      })
      .then(function (cd) {
        updateCartBadge(cd.item_count);
        return fetch("/?sections=cart-drawer", { credentials: "same-origin" })
          .then(function (r) {
            return r.json();
          })
          .then(function (s) {
            var h = s["cart-drawer"];
            if (h) {
              var d = document.querySelector("cart-drawer");
              if (d) {
                var p = new DOMParser();
                var doc = p.parseFromString(h, "text/html");
                var n = doc.querySelector("cart-drawer");
                if (n) d.replaceWith(n);
                else d.innerHTML = h;
              }
            }
            fireCartEvents(cd);
          })
          .catch(function () {
            fireCartEvents(cd);
          });
      });
  }

  /*
    Updates all known cart badge variants used by the theme/header. The helper
    also creates a native-looking badge if the cart previously had no items and
    no badge element was rendered.
  */
  function updateCartBadge(count) {
    var safeCount = Math.max(0, Math.floor(Number(count) || 0));
    var legacyBubbles = document.querySelectorAll(".cg-cart-count-bubble");
    for (var l = 0; l < legacyBubbles.length; l++) {
      legacyBubbles[l].remove();
    }
    var bubbles = Array.prototype.slice.call(
      document.querySelectorAll(".cart-count-bubble,[data-cart-count-bubble]")
    );
    var statuses = document.querySelectorAll(".header__cart__status");
    for (var i = 0; i < statuses.length; i++) {
      statuses[i].textContent = "";
    }
    if (safeCount <= 0) {
      for (var h = 0; h < bubbles.length; h++) {
        bubbles[h].remove();
      }
      return;
    }
    if (!bubbles.length) {
      var headerCart = document.querySelector("#cart-icon-bubble");
      if (headerCart) {
        var nativeBubble = document.createElement("span");
        nativeBubble.className = "cart-count-bubble";
        var visibleCount = document.createElement("span");
        visibleCount.setAttribute("aria-hidden", "true");
        var hiddenCount = document.createElement("span");
        hiddenCount.className = "visually-hidden";
        nativeBubble.appendChild(visibleCount);
        nativeBubble.appendChild(hiddenCount);
        headerCart.appendChild(nativeBubble);
        bubbles.push(nativeBubble);
      }
    }
    for (var b = 0; b < bubbles.length; b++) {
      var bubble = bubbles[b];
      if (!bubble) continue;
      var numberNode =
        bubble.querySelector('[aria-hidden="true"]') ||
        bubble.querySelector("[data-cart-count]") ||
        bubble.querySelector(".cart-link__bubble-num") ||
        bubble.querySelector(".cart-count");
      if (!numberNode) {
        numberNode = document.createElement("span");
        numberNode.setAttribute("aria-hidden", "true");
        bubble.insertBefore(numberNode, bubble.firstChild);
      }
      var hiddenText = bubble.querySelector(".visually-hidden");
      if (!hiddenText) {
        hiddenText = document.createElement("span");
        hiddenText.className = "visually-hidden";
        bubble.appendChild(hiddenText);
      }
      numberNode.textContent = safeCount;
      hiddenText.textContent =
        safeCount + " item" + (safeCount === 1 ? "" : "s");
      bubble.hidden = false;
      bubble.removeAttribute("hidden");
      bubble.classList.remove("hidden", "is-empty");
      bubble.classList.add("cart-count-bubble");
      bubble.style.display = "";
    }
  }

  /*
    Dispatches standard cart events for any theme scripts that listen for cart
    updates.
  */
  function fireCartEvents(cd) {
    ["cart:updated", "cart:change", "cart:refresh"].forEach(function (e) {
      var ev = new CustomEvent(e, { detail: cd, bubbles: true });
      document.dispatchEvent(ev);
      window.dispatchEvent(ev);
    });
  }

  /*
    Error hook for initialization failures. It currently only hides the loader
    because visible error messaging is handled by storefront QA requirements.
  */
  function showError(m) {
    setCgLoading(false);
  }

  /*
    Entry point.

    The asset auto-initializes when loaded. On direct Skin Analysis page loads
    this happens after DOMContentLoaded; on preloaded home-page flows global.js
    ensures the real Skin Analysis container exists before this asset executes.
  */
  function autoInit() {
    console.log("[CG] >>> CHECKPOINT 4-pre: autoInit called");
    setCgLoading(true);
    removeAllDebugOverlays();
    initSDK().catch(function (e) {
      console.error("[CG] !!! initSDK failed:", e);
      showError("Failed to load. Please reload.");
    });
  }
  removeAllDebugOverlays();
  setTimeout(removeAllDebugOverlays, 300);
  setTimeout(removeAllDebugOverlays, 1200);
  console.log("[CG] >>> readyState:", document.readyState);
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", autoInit);
  else autoInit();
  console.log("[CG] >>> CHECKPOINT 12: IIFE end");
})();
